// Pull-to-Refresh auf iOS deaktivieren - NUR für Stadtgrafik
// Die allgemeine Pull-to-Refresh-Verhinderung wurde entfernt,
// da sie nur auf der Stadtgrafik angewendet werden soll

// iOS Viewport Height Fix - Berechnet die tatsächliche Viewport-Höhe
// Dies behebt das Problem, dass 100vh auf iOS die Browser-UI (Tab-Leiste) einschließt
function setViewportHeight() {
  const vh = window.innerHeight * 0.01;
  const vw = window.innerWidth * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  document.documentElement.style.setProperty('--vw', `${vw}px`);
}

// Initial setzen
setViewportHeight();

// Bei Resize und Orientation Change aktualisieren
window.addEventListener('resize', setViewportHeight);
window.addEventListener('orientationchange', () => {
  // Kleine Verzögerung für iOS, damit die Orientation Change abgeschlossen ist
  setTimeout(setViewportHeight, 100);
});

// Drag-Variablen (global für Zugriff von Hotspot-Handlern)
let isDragging = false;

// Stellt sicher, dass die Lottie-Library verfügbar ist (inkl. Fallback-CDN) – wichtig für Mobile
let lottieReadyPromise = null;
function ensureLottieReady() {
  if (window.bodymovin || window.lottie) {
    return Promise.resolve(window.bodymovin || window.lottie);
  }

  if (!lottieReadyPromise) {
    lottieReadyPromise = new Promise(resolve => {
      const finish = () => resolve(window.bodymovin || window.lottie || null);

      // Falls bereits ein Lade-Script vorhanden ist, darauf horchen
      const existingLoader = document.querySelector('script[data-lottie-loader]');
      if (existingLoader) {
        existingLoader.addEventListener('load', finish, { once: true });
        existingLoader.addEventListener('error', () => resolve(null), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'assets/js/lottie.min.js';
      script.async = true;
      script.dataset.lottieLoader = 'local';
      script.onload = finish;
      script.onerror = () => {
        // Fallback auf CDN, falls die lokale Datei z.B. auf Mobile nicht geladen wird
        const fallback = document.createElement('script');
        fallback.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.13.0/lottie.min.js';
        fallback.async = true;
        fallback.dataset.lottieLoader = 'cdn';
        fallback.onload = finish;
        fallback.onerror = () => resolve(null);
        document.head.appendChild(fallback);
      };
      document.head.appendChild(script);
    });
  }

  return lottieReadyPromise;
}

// Funktion zum Laden und Anpassen der VTT-Datei für mobile Geräte
async function loadVTTWithMobileAdjustment(vttPath) {
  const isMobile = window.innerWidth <= 768;
  const normalizedPath = vttPath.toLowerCase();
  
  try {
    const response = await fetch(normalizedPath);
    let vttContent = await response.text();
    
    // Für mobile Geräte: line:92% zu line:85% ändern
    if (isMobile) {
      vttContent = vttContent.replace(/line:92%/g, 'line:85%');
    }
    
    // Blob-URL erstellen
    const blob = new Blob([vttContent], { type: 'text/vtt' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Fehler beim Laden der VTT-Datei:', error);
    // Fallback: normalisierter Pfad (kleingeschrieben für AEM)
    return normalizedPath;
  }
}

function dedupeVideoTracks(video) {
  if (!video) return null;

  const tracks = [...video.querySelectorAll('track')];
  if (!tracks.length) return null;

  const keep = tracks.find(track =>
    (track.getAttribute('src') || '').toLowerCase().includes('251201_untertitel.vtt')
  ) || tracks[0];

  tracks.forEach(track => {
    if (track !== keep) track.remove();
  });

  const src = keep.getAttribute('src');
  if (src) {
    keep.setAttribute('src', src.toLowerCase());
  }

  return keep;
}

function enhanceExistingVttTrack(video) {
  const track = dedupeVideoTracks(video);
  if (!track || video.dataset.vttEnhanced === 'true') return;

  const fetchPath = (track.getAttribute('src') || '').toLowerCase();
  if (!fetchPath) return;

  video.dataset.vttEnhanced = 'true';

  loadVTTWithMobileAdjustment(fetchPath).then(vttUrl => {
    if (track.isConnected) {
      track.src = vttUrl;
    }
  });
}

function attachVttTrack(video, vttPath) {
  if (!video) return;

  let track = dedupeVideoTracks(video);

  if (!track) {
    track = document.createElement('track');
    track.kind = 'captions';
    track.srclang = 'de';
    track.label = 'Deutsch';
    track.default = true;
    track.src = vttPath.toLowerCase();
    video.appendChild(track);
    video.dataset.vttEnhanced = 'true';
  }

  const fetchPath = (track.getAttribute('src') || vttPath || '').toLowerCase();

  loadVTTWithMobileAdjustment(fetchPath).then(vttUrl => {
    if (track.isConnected) {
      track.src = vttUrl;
    }
  });
}

// Hotspot Inhalte
const hotspotContents = {
	"Drohne": {
	  html: `
		<h2>Drohnen</h2>
		<p>
      Damit das Stromnetz den wachsenden Anforderungen der Zukunft standhält, wird es immer digitaler. Sensoren, intelligente Mess- und Regeltechnik sowie moderne Kommunikationstechnologien ermöglichen es, den Zustand der Netze in Echtzeit zu überwachen – oft sogar aus der Ferne. 
      <br><br>
      Bei uns sind dafür zum Beispiel Drohnen im Einsatz, die Leitungen schnell, sicher und präzise aus der Luft kontrollieren. Die Fluggeräte liefern wertvolle Daten und zeigen, wie moderne Technik die Netzüberwachung effizienter macht. <br><br>
      Mit diesen Informationen können wir unsere Netze noch gezielter steuern, frühzeitig reagieren und sie optimal auf die Herausforderungen der Energiewende ausrichten.
		</p>
    <p class="extra-text">
      Sensoren, Smart Meter, Drohen: Die Digitalisierung der Energienetze ist in vollem Gange. 
      <a href="https://www.westenergie.de/de/ueber-westenergie/verteilnetze/digitalisierung.html" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
	  `,
	  // Optionales Fallback-Bild (wird nur genutzt, wenn Lottie nicht verfügbar ist)
	  // img: "assets/images/hotspots/hotspot_solor_boden.png",
	  // imgDark: "assets/images/hotspots/hotspot_solor_boden_dark.png",
	  lottie: {
		path: "assets/images/hotspots/Drohne/Drohne.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Drohne_Dark/Drohne_Dark.json"
	  }
	},
	"Hoechstspannungsmast": {
	  html: `
		<h2>Höchstspannung (380/220 kV)</h2>
		<p>
		  Das Rückgrat des Stromnetzes: Mit 220 oder 380 Kilovolt transportieren Übertragungsnetzbetreiber wie Amprion oder TenneT Strom aus Großkraftwerken und Offshore-Windparks quer durch Deutschland und Europa. Vom Übertragungsnetz gelangt der Strom in die Verteilnetze – und damit näher zu den Verbraucher*innen. 
		</p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Hoechstspannungsmast/Hoechstspannungsmast.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Hoechstspannungsmast_Dark/Hoechstspannungsmast_Dark.json"
	  }
	},
	"Ladebordstein": {
	  html: `
		<h2>Ladebordstein</h2>
		<p>
		  Hier steckt mehr drin als Asphalt: Im Ladebordstein ist die Technik fürs E-Auto direkt am Straßenrand verbaut – unsichtbar, platzsparend und ideal für dicht bebaute Stadtviertel ohne eigene Stellplätze. So machen Kommunen E-Mobilität für alle zugänglich.  
		</p>
    <p class="extra-text">
      Wie der Ladebordstein in der Gemeinde Nörvenich funktioniert?
      <a href="https://www.westenergie.de/newsroom/article/rheinmetall-startet-gemeinsam-mit-der-gemeinde-noervenich-und-der-westenergie-ag-weiteres-pilotprojekt-zu-innovativen-ladebordsteinen-im-oeffentlichen-raum" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
		<p class="extra-text">
		  <i>
		  „Gerade für Menschen in Wohnungen oder Einfamilienhäusern ohne Stellplätze auf eigenem Grundstück ist der Einstieg in die Elektromobilität oft schwierig. Diese können weder eine Wallbox anbringen, noch dürfen sie ein Kabel über den Gehweg zum Laden ihres Fahrzeugs verlegen. Genau hier ist der Ladebordstein eine ideale Lösung, weil eine Lademöglichkeit direkt am Stellplatz im öffentlichen Raum angeboten wird, ohne dass die sonst übliche Ladesäule auf dem Gehweg Platz finden muss und den Raum für die Fußgänger weiter einschränkt.“ <br><br>
		  <br><br>
		  Dr. Timo Czech, Bürgermeister Nörvenich 
		  </i>
		</p>
		`,
	  lottie: {
		path: "assets/images/hotspots/Ladebordstein/Ladebordstein.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Ladebordstein_Dark/Ladebordstein_Dark.json"
	  }
	},
	"Ladesaeulen": {
	  html: `
		<h2>E-Auto-Ladesäule</h2>
		<p>
		  Ob Rathaus, Parkhaus oder Stadtzentrum: Öffentliche Ladesäulen sind entscheidend für den Umstieg auf klimafreundliche Autos. Kommunen, die in öffentliche Ladepunkte investieren, stärken nachhaltige Mobilität – und machen ihre Stadt attraktiver für Bürger*innen und Besucher*innen. 
		</p>
    <p class="extra-text">
      So schaffen wir Lösungen für nachhaltige Mobilität. 
      <a href="https://www.westenergie.de/de/loesungen/mobilitaet/parken-und-laden.html" target="_blank" data-icon="handshake">Hier informieren</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Ladesaeulen/Ladesaeulen.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Ladesaeulen_Dark/Ladesaeulen_Dark.json"
	  }
	},
	"Mittelspannungsmasten": {
	  html: `
		<h2>Mittelspannung <br>(10-30 kV)</h2>
		<p>
		  In einer weiteren Umspannanlage wird der Strom auf 10 bis 30 Kilovolt transformiert und in regionale Mittelspannungsnetze eingespeist. Hier fließt der Strom zu mittelständischen Unternehmen – und nimmt gleichzeitig Energie aus Wind- und Solaranlagen auf.
		</p>
    <p class="extra-text">
    Bestens vernetzt: So funktionieren die verschiedenen Spannungsebenen. 
    <a href="https://www.westenergie.de/de/ueber-westenergie/verteilnetze/stromnetz.html#1a8qh8n1d0insuyptd2z" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Mittelspannungsmasten/Mittelspannungsmasten.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Ladesaeulen_Dark/Ladesaeulen_Dark.json"
	  }
	},
	"Netzleitstelle": {
	  html: `
		<h2>Netzleitstelle</h2>
    <p>
		  Das Hirn des Stromnetzes: In der Schaltzentrale wird alles überwacht, koordiniert und gesteuert – von Stromflüssen über Spannungslagen bis hin zu Störungen. Hier treffen Daten aus dem gesamten Netz zusammen.   
		  <br><br>
		  Und die Zukunft? Die heißt Smart Grid: Mit digitaler Technik wird das Netz noch flexibler und kann auf Schwankungen automatisch reagieren – in Echtzeit und immer zuverlässiger. Das unterstützt auch die Klimaziele in Deutschland: Bis 2030 soll der Anteil erneuerbarer Energien am Stromverbrauch auf mindestens 80 Prozent steigen. Eine große Herausforderung, vor allem für das Verteilnetz – denn hier treffen immer mehr Verbraucher*innen, Solaranlagen, Speicher und Ladepunkte aufeinander. Damit das reibungslos klappt, braucht es ein Netz, das nicht nur leitet, sondern mitdenkt.  
		</p>
		<p class="extra-text">
      Mehr als Schalten und Walten: So denkt das Netz digital.
		  <a href="https://www.westenergie.de/de/ueber-westenergie/verteilnetze/digitalisierung/smartgrid.html" target="_blank" data-icon="questionmark">Weiterlesen</a>
		</p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Netzleitstelle/Netzleitstelle.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Netzleitstelle_Dark/Netzleitstelle_Dark.json"
	  }
	},
	"Niederspannungsmast": {
	  html: `
		<h2>Niederspannung <br>(400 V)</h2>
		<p>
		  Das Ortsnetz bringt Strom bis zu privaten Haushalten, kleineren Betriebe und Gewerben sowie den öffentlichen Einrichtungen wie Schulen oder Turnhallen. In Ortsnetzstationen wird der Strom auf haushaltsübliche 230/400 Volt transformiert und direkt an die Endverbraucher*innen verteilt. Das Ortsnetz versorgt nicht nur, sondern nimmt auch Strom auf – etwa von PV-Anlagen auf dem Dach.  
		</p>
    <p class="extra-text">
      In Deutschland gibt es über eine Million Strommasten. Aber sind wirklich alle gleich? 
      <a href="https://www.westenergie.de/de/ueber-westenergie/verteilnetze/stromnetz/strommast.html" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Niederspannungsmast/Niederspannungsmast.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Niederspannungsmast_Dark/Niederspannungsmast_Dark.json"
	  }
	},
	"Ortsnetzstation": {
	  html: `
		<h2>Digitale Ortsnetzstation <br>(digiONS)</h2>
		<p>
		  Die digitalen Ortsnetzstationen denken mit: Sie erkennen Störungen früh, steuern den Stromfluss lokal – und machen das Netz fit für die Energiewende.
		</p>
    <p class="extra-text">
      Warum sind unsere digiONS eingentlich so schlau? 
      <a href="https://www.westenergie.de/de/ueber-westenergie/verteilnetze/stromnetz/ortsnetzstation.html" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
    <p class="extra-text">
      Von Planung bis Wartung: Wir bieten die Komplettlösung für betriebliche Trafostationen 
      <a href="https://www.westenergie.de/netzservice/de/produkte/produkte/trafostationen.html" target="_blank" data-icon="handshake">Hier informieren</a>   
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Ortsnetzstation/Ortsnetzstation.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Ortsnetzstation_Dark/Ortsnetzstation_Dark.json"
	  }
	},
	"Privathaushalt": {
	  html: `
		<h2>PV-Anlage (privat)</h2>
		<p>
		  Sonnenstrom vom eigenen Dach spart Kosten und schützt das Klima. Wird mehr Strom erzeugt als verbraucht, fließt er ins öffentliche Netz – ganz ohne CO₂-Emissionen. 
		</p>
		<h2>Stromspeicher (privat)</h2>
		<p>
		  Ein Stromspeicher macht den Solarstrom vom eigenen Dach auch abends oder bei schlechtem Wetter nutzbar – und erhöht so den Eigenverbrauch. Das macht unabhängiger vom Netz und senkt die Kosten. Am häufigsten kommen Lithium-Ionen-Batterien zum Einsatz: effizient, platzsparend und langlebig. 
		</p>
		<h2>Wallbox (privat)</h2>
		<p>
		  Mit der eigenen Wallbox wird das Zuhause zur Ladestation: Einfach, sicher und schneller als an der Steckdose. Perfekt für alle, die ihr E-Auto regelmäßig bequem über Nacht laden wollen.   
		</p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Privathaushalt/Privathaushalt.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Privathaushalt_Dark/Privathaushalt_Dark.json"
	  }
	},
	"Umspannwerk": {
	  html: `
		<h2>Umspannwerk</h2>
		<p>
		  Hier wird Spannung angepasst: hoch oder runter – je nachdem, was das Netz gerade braucht. So fließt Strom effizient von einer Ebene zur nächsten. 
		</p>
    <p class="extra-text">
      Umspannwerke sind wichtige Verbindungsstellen in unseren Stromnetzen. Aber was passiert hier genau? 
      <a href="https://www.westenergie.de/umspannwerk" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
    <p class="extra-text">
      Umspannwerke und Schalthäuser: Wir sind der Partner für maßgeschneiderte energietechnische Anlagen 
      <a href="https://www.westenergie.de/netzservice/de/produkte/anlagenbau.html" target="_blank" data-icon="handshake">Hier informieren</a>  
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Umspannwerk/Umspannwerk.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Umspannwerk_Dark/Umspannwerk_Dark.json"
	  }
	},
	"Windkraft": {
	  html: `
		<h2>Windkraft</h2>
		<p>
      Ob an Land oder auf dem Meer: Windkraft ist ein zentraler Pfeiler der Energiewende. Während die großen Offshore-Anlagen in Nord- und Ostsee künftig noch mehr Strom ins Übertragungsnetz liefern sollen, versorgt die Windkraft an Land dezentral Regionen direkt dort, wo der Strom gebraucht wird.  
      <br><br>
      <b>Und was ist, wenn der Wind nicht weht?</b>
      <br><br>
		  Dass wir in Deutschland bereits gut die Hälfte des Strombedarfs aus Windkraft und Sonne decken, ist ein wichtiges Etappenziel auf dem Weg zur klimaneutralen Energieversorgung. Trotzdem fragen sich viele Menschen, was passiert, wenn in Zukunft noch mehr Strom aus Sonne und Wind gewonnen wird ... und es dann mal windstill ist oder die Sonne nicht scheint.
    </p>
    <p class="extra-text">
      Über 100 Jahre Netzerfahrung für Ihren Windpark: Wir übernehmen Consulting Engineering und Netzführung. 
      <a href="https://www.westenergie.de/netzservice/de/produkte/produkte/offshore.html" target="_blank" data-icon="handshake">Hier informieren</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Windrad/Windrad.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Windrad_Dark/Windrad_Dark.json"
	  }
	},
	"PV-Anlagen": {
	  html: `
		<h2>PV-Freiflächenanlagen</h2>
		<p>
		  Große Solaranlagen auf Wiesen, Feldern oder Brachflächen liefern jede Menge Strom. Photovoltaik-Freiflächenanlagen gibt es in Deutschland von kleineren Anlagen bis hin zu großen Solarparks von mehreren Hundert Hektar. Durch optimale Ausrichtung der Module zur Sonne erzeugen sie pro Hektar bis zu 500.000 Kilowattstunden im Jahr.
		</p>
    <p class="extra-text">
      So machen wir Städte und Gemeinden zu Sonnen-Stromerzeugern. 
      <a href="https://www.westenergie.de/de/loesungen/nachhaltigkeit/photovoltaik-kommunal.html" target="_blank" data-icon="handshake">Hier informieren</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/PV-Anlagen/PV-Anlagen.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/PV-Anlagen_Dark/PV-Anlagen_Dark.json"
	  }
	},
	"Kraftwerk": {
	  html: `
		<h2>Fossile Kraftwerke</h2>
		<p>
		  Kohle, Gas, Öl – fossile Kraftwerke erzeugen Strom durch Wasserdampf, der Turbinen antreibt. Das ist zwar technisch bewährt, verursacht aber hohe CO₂-Emissionen. Deshalb werden sie in Deutschland nach und nach durch klimafreundliche Alternativen ersetzt.
    </p>
    <p class="extra-text">
      Wir bieten individuelle Lösungen für effiziente Infrastrukturprojekte
      <a href="https://www.westenergie.de/netzservice/de/produkte/produkte/dienstleistungenkraftwerke.html" target="_blank" data-icon="handshake">Hier informieren</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Kraftwerk/Kraftwerk.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Kraftwerk_Dark/Kraftwerk_Dark.json"
	  }
	},
	"Kabelverteilerschrank": {
	  html: `
		<h2>Kabelverteilerschrank</h2>
		<p>
		  Von hier geht’s weiter: Verteilerkästen bringen den Strom direkt zu den Häusern – sicher und zuverlässig.
		</p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Kabelverteilerschrank/Kabelverteilerschrank.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Kabelverteilerschrank_Dark/Kabelverteilerschrank_Dark.json"
	  }
	},
	"Hochspannungsmast": {
	  html: `
		<h2>Hochspannung <br>(110 kV)</h2>
		<p>
		  Über Umspannwerke wird die Spannung auf 110 Kilovolt reduziert. Auf dieser Ebene verläuft der Strom durch überregionale Hochspannungsnetze – zu Großkund*innen der Industrie oder Versorgungsunternehmen wie Stadtwerken.
		</p>
    <p class="extra-text">
      Wie hoch ist eigentlich ein Strommast?  
      <a href="https://www.westenergie.de/strommast" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
    <p class="extra-text">
    Planung, Bau, Betrieb und Instandhaltung. Unser Netzdienstleistungen für zuverlässige Versorgung. 
    <a href="https://www.westenergie.de/netzservice/de/produkte/leitungsinfrastruktur.html" target="_blank" data-icon="handshake">Hier informieren</a>  
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Hochspannungsmast/Hochspannungsmast.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Hochspannungsmast_Dark/Hochspannungsmast_Dark.json"
	  }
	},
	"Batteriespeicher": {
	  html: `
		<h2>Batteriespeicher</h2>
		<p>
		  Energiewende im XXL-Format: Stromspeicher gleichen Schwankungen bei Sonne und Wind aus. Sie machen die Nutzung von erneuerbaren Energien so planbarer – und Strom langfristig günstiger. Denn wenn viel Strom im Netz vorhanden ist, können Großspeicher diesen zwischenspeichern und bei plötzlichen Belastungsspitzen oder nachts, wenn keine Solarenergie mehr erzeugt wird, wieder abgeben. Anders als Wasserkraft- oder Pumpspeicher, die üblicherweise ans Netz gehen, um hohe Belastung abzufedern, sind Batteriespeicher schwarzstartfähig. Das bedeutet, sie müssten nach einem Blackout nicht erst hochgefahren werden, sondern könnten sofort Strom liefern.   
		</p>
    <p class="extra-text">
      Batteriespeicher ermöglichen eine flexible Energieversorgung. Aber wir funktioniert das genau? 
      <a href="https://www.westenergie.de/de/ueber-westenergie/verteilnetze/stromnetz/batteriespeicher.html" target="_blank" data-icon="questionmark">Weiterlesen</a>
    </p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Batteriespeicher/Batteriespeicher.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Batteriespeicher_Dark/Batteriespeicher_Dark.json"
	  }
	},
  "Kommunen": {
	  html: `
		<h2>Nachhaltige Beleuchtung für Kommunen</h2>
		<p>
      Energiekosten senken, Klimaschutz stärken: Mit moderner LED-Innenbeleuchtung können Kommunen den Stromverbrauch in Gebäuden wie Schulen, Kitas, Museen oder Verwaltungsgebäuden um bis zu 80 Prozent reduzieren. Gleichzeitig sinken Wartungs- und Betriebskosten – und die kommunalen Klimaziele rücken ein gutes Stück näher. 
      <br><br>
      Präzise geplant, effizient umgesetzt: Wir begleiten Kommunen durch alle Projektphasen und realisieren nachhaltige Beleuchtungskonzepte aus einer Hand. 
		</p>
		<p>
		  <a href="https://www.westenergie.de/de/loesungen/nachhaltigkeit/innenraumbeleuchtung-fuer-kommunen.html" target="_blank" data-icon="handshake">Hier informieren</a>
		</p>
	  `,
	  lottie: {
		path: "assets/images/hotspots/Schule/Schule.json"
	  },
	  lottieDark: {
		path: "assets/images/hotspots/Schule_Dark/Schule_Dark.json"
	  }
	}
}
// Aktuelle Hotspot-/Lottie-Referenzen für Overlay-Hintergründe (z.B. Drohne)
let currentOverlayLottie = null;

// Aktuelle Hintergrund-Lottie-Animationen (Stadt - beide parallel)
let currentBackgroundLottie = null;
let currentBackgroundLottieDark = null;
let syncInterval = null;

// Funktion zur Synchronisation beider Animationen
function syncLottieAnimations() {
  if (!currentBackgroundLottie || !currentBackgroundLottieDark) return;
  
  // Verwende die sichtbare Animation als Master
  const lottieContainer = document.getElementById('background-lottie');
  const isDarkVisible = lottieContainer && lottieContainer.style.display === 'none';
  
  if (isDarkVisible) {
    // Dark ist sichtbar: Dark ist Master, synchronisiere Tag
    const currentTime = currentBackgroundLottieDark.currentFrame;
    if (Math.abs(currentBackgroundLottie.currentFrame - currentTime) > 1) {
      currentBackgroundLottie.goToAndStop(currentTime, true);
    }
  } else {
    // Tag ist sichtbar: Tag ist Master, synchronisiere Dark
    const currentTime = currentBackgroundLottie.currentFrame;
    if (Math.abs(currentBackgroundLottieDark.currentFrame - currentTime) > 1) {
      currentBackgroundLottieDark.goToAndStop(currentTime, true);
    }
  }
}

// Funktion zum Laden beider Hintergrund-Lottie-Animationen parallel
async function loadBackgroundLotties() {
  const lottieContainer = document.getElementById('background-lottie');
  const lottieContainerDark = document.getElementById('background-lottie-dark');
  
  if (!lottieContainer || !lottieContainerDark) return;

  // Lottie-Library sicherstellen
  const lottieLib = await ensureLottieReady();
  if (!lottieLib || !lottieLib.loadAnimation) {
    console.error('Lottie-Library konnte nicht geladen werden');
    return;
  }

  // Beide Animationen parallel laden
  try {
    // Tag-Animation laden
    if (!currentBackgroundLottie) {
      const lottieInstance = lottieLib.loadAnimation({
        container: lottieContainer,
        path: 'assets/images/stadt/data.json',
        renderer: 'svg',
        loop: true,
        autoplay: true
      });

      if (lottieInstance) {
        currentBackgroundLottie = lottieInstance;
        lottieInstance.addEventListener('DOMLoaded', () => {
          lottieInstance.play();
        });
      }
    }

    // Dark-Animation laden
    if (!currentBackgroundLottieDark) {
      const lottieInstanceDark = lottieLib.loadAnimation({
        container: lottieContainerDark,
        path: 'assets/images/stadt_dark/data.json',
        renderer: 'svg',
        loop: true,
        autoplay: true
      });

      if (lottieInstanceDark) {
        currentBackgroundLottieDark = lottieInstanceDark;
        
        // Synchronisiere beide Animationen nach dem Laden
        const syncAnimations = () => {
          if (currentBackgroundLottie && currentBackgroundLottieDark) {
            // Setze Dark-Animation auf die gleiche Zeit wie Tag-Animation
            const currentTime = currentBackgroundLottie.currentFrame;
            lottieInstanceDark.goToAndStop(currentTime, true);
            lottieInstanceDark.play();
            
            // Starte kontinuierliche Synchronisation
            if (syncInterval) {
              clearInterval(syncInterval);
            }
            syncInterval = setInterval(syncLottieAnimations, 100); // Alle 100ms synchronisieren
          }
        };
        
        lottieInstanceDark.addEventListener('DOMLoaded', () => {
          syncAnimations();
        });
        
        // Auch nach dem ersten Frame synchronisieren
        setTimeout(syncAnimations, 200);
      }
    } else {
      // Falls Dark bereits geladen ist, starte Synchronisation
      if (currentBackgroundLottie && currentBackgroundLottieDark) {
        if (syncInterval) {
          clearInterval(syncInterval);
        }
        syncInterval = setInterval(syncLottieAnimations, 100);
      }
    }

    // Skalierung prüfen nach dem Laden
    setTimeout(() => {
      checkIfImageIsCropped();
    }, 500);
  } catch (err) {
    console.error('Fehler beim Laden der Hintergrund-Lottie-Animationen:', err);
  }
}

// Funktion zum Wechseln zwischen Tag und Dark Mode (nur Sichtbarkeit ändern)
function switchBackgroundLottie(isDark = false) {
  const lottieContainer = document.getElementById('background-lottie');
  const lottieContainerDark = document.getElementById('background-lottie-dark');
  
  if (!lottieContainer || !lottieContainerDark) return;

  if (isDark) {
    // Dark Mode: Zeige Dark-Animation, verstecke Tag-Animation
    lottieContainer.style.display = 'none';
    lottieContainerDark.style.display = 'block';
    
    // Synchronisiere Dark-Animation mit Tag-Animation (sofort)
    if (currentBackgroundLottie && currentBackgroundLottieDark) {
      const currentTime = currentBackgroundLottie.currentFrame;
      currentBackgroundLottieDark.goToAndStop(currentTime, true);
      currentBackgroundLottieDark.play();
    }
  } else {
    // Tag Mode: Zeige Tag-Animation, verstecke Dark-Animation
    lottieContainer.style.display = 'block';
    lottieContainerDark.style.display = 'none';
    
    // Synchronisiere Tag-Animation mit Dark-Animation (sofort)
    if (currentBackgroundLottie && currentBackgroundLottieDark) {
      const currentTime = currentBackgroundLottieDark.currentFrame;
      currentBackgroundLottie.goToAndStop(currentTime, true);
      currentBackgroundLottie.play();
    }
  }
  
  // Die kontinuierliche Synchronisation läuft weiter im Hintergrund
}
let currentOverlayHotspotKey = null;

// Gemeinsame Funktion für Hotspot-Interaktionen (Click und Touch)
async function handleHotspotInteraction(hotspotElement, e) {
  // Verhindere Interaktion während des Draggings
  if (isDragging) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    return;
  }
  
  // Verhindere Event-Propagation, damit Drag-Logik nicht ausgelöst wird
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  const key = hotspotElement.getAttribute('data-hotspot');
  const stromtyp = hotspotElement.getAttribute('data-stromtyp');
      const overlayContent = document.getElementById('overlayContent');
      const overlay = document.getElementById('overlay');
      const graphic = document.querySelector('.graphic-container');

      // aktuell geöffneter Hotspot-Key merken (für z.B. Darkmode-Umschaltung)
      currentOverlayHotspotKey = key;

      // Zoom mit 0.8s Transition (langsamer)
      const scale = 7;
      graphic.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
      graphic.classList.add('zooming');
      graphic.style.transform = `scale(${scale})`;
      // Transition nach Animation wieder zurücksetzen
      setTimeout(() => {
        graphic.style.transition = '';
      }, 800);

      // Overlay anzeigen (zoomIn Animation in CSS: 0.5s)
      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');
      
      // overlayContent mit 0.2s Fade-In (nach kurzem Delay)
      if (overlayContent) {
        overlayContent.style.transition = 'opacity 0.2s ease-out';
        overlayContent.style.opacity = '0';
        setTimeout(() => {
          overlayContent.style.opacity = '1';
          // Transition nach Animation zurücksetzen
          setTimeout(() => {
            overlayContent.style.transition = '';
            overlayContent.style.opacity = '';
          }, 200);
        }, 50); // Kleines Delay für besseren Effekt
      }

      // Vorheriges Bild/Lottie entfernen
      const oldBg = document.getElementById('overlay-bg-img');
      if (oldBg) oldBg.remove();
      const oldLottie = document.getElementById('overlay-bg-lottie');
      if (oldLottie) oldLottie.remove();
      if (currentOverlayLottie) {
        try {
          currentOverlayLottie.destroy();
        } catch (err) {
          console.error('Fehler beim Zerstören der Lottie-Animation:', err);
        }
        currentOverlayLottie = null;
      }

      // Overlay-Inhalt sofort setzen
      const hotspotHtml = (hotspotContents[key] && hotspotContents[key].html) || '';
      overlayContent.innerHTML = `<div class='infobox-scroll'>${hotspotHtml}</div>`;

      // Zurück-zur-Übersicht-Link mit SVG-Icon einfügen
      const backLink = document.createElement('a');
      backLink.href = '#';
      backLink.className = 'back-to-overview';
      backLink.setAttribute('aria-label', 'Zurück zur Übersicht');
      backLink.innerHTML = `
        <span class="icon-back" style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;margin-right:8px;">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="15" stroke="white" stroke-width="2"/>
            <polyline points="18,10 12,16 18,22" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        Zurück zur Übersicht
      `;
      overlayContent.insertBefore(backLink, overlayContent.firstChild);

      // Fokus auf Schließen-Button für Tastatur-Navigation
      requestAnimationFrame(() => {
        backLink.focus();
      });

      // Verortung im Stromnetz dynamisch einfügen
      const stromtypen = [
        { key: 'höchstspannung', label: 'Höchstspannung', svg: `<svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="has_fill"><path d="M12.9463 27.6233L17.7232 30.521L16.9009 25.2243L12.9463 27.6233ZM6.25188 30.5208L11.0284 27.6233L7.07619 25.2258L6.25188 30.5208ZM7.67859 24.428L11.9873 27.0416L16.296 24.428L11.9873 21.8922L7.67859 24.428ZM12.9677 21.3153L16.6287 23.4696L16.0157 19.5215L12.9677 21.3153ZM7.3499 23.4674L11.0069 21.3153L7.96363 19.5244L7.3499 23.4674ZM8.40008 18.6271L11.9873 20.7382L15.5745 18.6271L11.9873 15.2739L8.40008 18.6271ZM12.7187 14.5903L15.6798 17.3585L14.9295 12.5237L12.7187 14.5903ZM8.30169 17.3519L11.256 14.5903L9.05228 12.5302L8.30169 17.3519ZM16.386 15.3251H20.9524L16.0798 13.3519L16.386 15.3251ZM3.04684 15.3251H7.59574L7.90174 13.3584L3.04684 15.3251ZM9.50895 11.5899L11.9873 13.9065L14.4741 11.582L12.0472 9.70557L9.50895 11.5899ZM20.9169 13.0476V11.1217H15.7383L15.7891 11.4786L15.9037 12.218C15.9299 12.224 15.956 12.2317 15.9817 12.2421L23.688 15.3625L23.7284 15.3811C23.9257 15.4817 24.0343 15.6988 23.9903 15.9157C23.9434 16.1472 23.7359 16.3141 23.4949 16.3141H21.5785V18.24C21.5785 18.5132 21.3524 18.7346 21.0735 18.7346C20.7946 18.7345 20.5685 18.5131 20.5685 18.24V16.3141H16.5397L16.9019 18.6496C16.9024 18.6531 16.9032 18.6566 16.9036 18.6602L18.8861 31.4311C18.9157 31.6217 18.8295 31.8119 18.6652 31.9181C18.5008 32.0244 18.2884 32.0274 18.121 31.9259L11.9873 28.2052L5.8539 31.9259C5.68646 32.0274 5.4738 32.0244 5.30945 31.9181C5.14515 31.8118 5.05909 31.6215 5.08877 31.4309L7.06756 18.7169C7.0676 18.6681 7.07549 18.6201 7.08976 18.574L7.44163 16.3141H2.85328V18.24C2.85328 18.5132 2.62718 18.7346 2.34828 18.7346C2.06941 18.7345 1.84329 18.5131 1.84329 18.24V16.3141H0.505104C0.264168 16.3141 0.056713 16.1474 0.00972615 15.916C-0.037223 15.6845 0.0893871 15.4527 0.312032 15.3625L8.01566 12.2421C8.03635 12.2337 8.05748 12.2271 8.07854 12.2216L8.24991 11.1217H2.85328V13.0476C2.85328 13.3208 2.62718 13.5422 2.34828 13.5422C2.06941 13.5421 1.84329 13.3207 1.84329 13.0476V11.1217H0.505104C0.263042 11.1217 0.0550303 10.9534 0.00923299 10.7207C-0.0365466 10.4879 0.0927392 10.256 0.317457 10.168L8.95266 6.78509L11.5748 0.312216L11.5906 0.277445C11.6751 0.107943 11.8519 -0.000906622 12.0467 5.69071e-06C12.2546 0.00101907 12.4407 0.126638 12.5155 0.316562L15.056 6.76746L23.4582 10.1701L23.4989 10.1887C23.696 10.2894 23.8045 10.5066 23.7605 10.7236C23.7135 10.955 23.506 11.1217 23.2651 11.1217H21.9269V13.0476C21.9269 13.3208 21.7008 13.5422 21.4219 13.5422C21.143 13.5421 20.9169 13.3207 20.9169 13.0476ZM12.8785 9.08815L14.6208 10.4353L14.281 8.04672L12.8785 9.08815ZM9.4219 10.1549C9.49627 10.1776 9.56295 10.2168 9.61818 10.2679L11.2283 9.07221L9.76637 7.94193L9.4219 10.1549ZM15.5977 10.1327H20.7231L15.2843 7.9301L15.5977 10.1327ZM3.12673 10.1327H8.40378L8.74652 7.93106L3.12673 10.1327ZM10.0058 6.86718L12.0596 8.45504L14.0546 6.97367L12.0374 1.85178L10.0058 6.86718Z" fill="white" /></svg>` },
        { key: 'hochspannung', label: 'Hochspannung', svg: `<svg width="29" height="27" viewBox="0 0 29 27" fill="none" xmlns="http://www.w3.org/2000/svg" class="has_fill"><path d="M7.55185 25.0937L13.3255 21.3581L8.54621 18.2657L7.55185 25.0937ZM15.6435 21.3581L21.4175 25.0937L20.4231 18.2657L15.6435 21.3581ZM9.27894 17.2402L14.4847 20.6083L19.6904 17.2399L14.4856 13.9723L9.27894 17.2402ZM8.87518 16.0062L13.3008 13.2285L9.61685 10.9154L8.87518 16.0062ZM15.67 13.2288L20.0938 16.0059L19.3528 10.9173L15.67 13.2288ZM10.1508 9.76355L14.4856 12.4849L18.8215 9.76355L14.4861 5.44121L10.1508 9.76355ZM24.8562 9.26465V6.78198H19.9851L20.4142 9.72838C20.4218 9.76107 20.4261 9.7945 20.4288 9.82829L22.823 26.2668C22.8588 26.5125 22.7546 26.7575 22.556 26.8945C22.3574 27.0315 22.1007 27.0354 21.8984 26.9045L14.4847 22.1078L7.07091 26.9045C6.8686 27.0353 6.61185 27.0315 6.41326 26.8945C6.21475 26.7575 6.11049 26.5124 6.14627 26.2668L8.98454 6.78198H3.44806V9.26465C3.44801 9.61663 3.17481 9.90205 2.8378 9.90205C2.50082 9.90201 2.22759 9.61661 2.22754 9.26465V6.78198H0.610397C0.319217 6.78198 0.068514 6.56705 0.0117547 6.26876C-0.0449827 5.97046 0.108008 5.67169 0.377079 5.55543L9.68658 1.53342C9.70819 1.52409 9.73018 1.51636 9.75213 1.50977L9.89338 0.541538L9.9044 0.484272C9.97101 0.202332 10.214 0 10.4968 0H14.4572L14.4847 0.00155614C14.4938 0.0011333 14.5029 2.02669e-06 14.5121 0H18.4728L18.5288 0.00280106C18.8057 0.0292504 19.0337 0.249602 19.0762 0.541538L19.216 1.50168C19.2487 1.50953 19.2814 1.5196 19.3134 1.53342L19.2455 1.70429V1.7046L19.3134 1.53342L28.6229 5.55543L28.6718 5.57939C28.9102 5.70906 29.0415 5.98906 28.9882 6.26876C28.9315 6.56705 28.6808 6.78198 28.3896 6.78198H26.0767V9.26465C26.0766 9.61663 25.8034 9.90205 25.4664 9.90205C25.1294 9.90204 24.8562 9.61662 24.8562 9.26465ZM10.0224 8.1296L13.6026 4.56012L10.9303 1.896L10.0224 8.1296ZM15.3697 4.56012L18.946 8.12587L18.039 1.89881L15.3697 4.56012ZM19.7994 5.50719H25.318L19.4287 2.9629L19.7994 5.50719ZM3.68198 5.50719H9.17018L9.53848 2.9769L3.68198 5.50719ZM14.4572 1.27479H12.0746L14.4861 3.67903L16.898 1.27479H14.5121C14.5029 1.27479 14.4938 1.27366 14.4847 1.27324L14.4572 1.27479Z" fill="white" /></svg>` },
        { key: 'mittelspannung', label: 'Mittelspannung', svg: `<svg width="18" height="27" viewBox="0 0 18 27" fill="none" xmlns="http://www.w3.org/2000/svg" class="no_fill"><path d="M8.62506 25.625L8.62506 0.625M3.45548 7.29169V5.93264L14.4326 5.96029V7.29167M0.625 4.37502L0.625 2.66304H17.125V4.79167" stroke="white" stroke-width="1.25" stroke-linecap="round"/></svg>` },
        { key: 'niederspannung', label: 'Niederspannung', svg: `<svg width="18" height="26" viewBox="0 0 18 26" fill="none" xmlns="http://www.w3.org/2000/svg" class="no_fill"><path d="M0.625 0.625V2.625" stroke="white" stroke-width="1.25" stroke-linecap="round"/><path d="M3.125 0.625V2.625" stroke="white" stroke-width="1.25" stroke-linecap="round"/><path d="M17.125 0.625V2.625" stroke="white" stroke-width="1.25" stroke-linecap="round"/><path d="M9.125 25.125V1.125" stroke="white" stroke-width="1.25" stroke-linecap="round"/><path d="M0.625 0.625L17.125 0.625" stroke="white" stroke-width="1.25" stroke-linecap="round"/></svg>` }
      ];
      const stromtypValue = stromtyp || '';
      // Unterstützung für mehrere Stromtypen (durch Leerzeichen getrennt)
      const stromtypValues = stromtypValue ? stromtypValue.split(' ') : [];
      const verortungDiv = document.createElement('div');
      verortungDiv.className = 'stromnetz-verortung';
      verortungDiv.innerHTML = `
        <div class="stromnetz-verortung-label">Verortung im Stromnetz:</div>
        <div class="stromnetz-verortung-icons">
          ${stromtypen.map(t => {
            const isActive = stromtypValue === 'alle-typen' || 
                           stromtypValue === t.key || 
                           stromtypValues.includes(t.key);
            return `<span class="stromnetz-verortung-icon${isActive ? ' active' : ''}">${t.svg}<span>${t.label}</span></span>`;
          }).join('')}
        </div>
      `;
      overlayContent.appendChild(verortungDiv);

      // NEU: Nur den spezifischen Hotspot-Namen als Klasse hinzufügen
      overlayContent.className = 'overlay-content ' + key;
      if (stromtyp) {
        overlayContent.setAttribute('data-stromtyp', stromtyp);
      } else {
        overlayContent.removeAttribute('data-stromtyp');
      }

      // Hintergrund: Lottie-Animation (z.B. Drohne) oder Bild einfügen und weich einblenden
      const hotspotConfig = hotspotContents[key] || {};
      const container = document.querySelector('.stromnetz-container');

      // Helper für Bild-Fallback, falls Lottie nicht verfügbar ist
      const renderBackgroundImage = () => {
        let bgImg = hotspotConfig.img;
        if (container && container.classList.contains('dark') && hotspotConfig.imgDark) {
          bgImg = hotspotConfig.imgDark;
        }
        if (bgImg) {
          const img = document.createElement('img');
          img.src = bgImg;
          img.className = 'overlay-bg-img';
          img.id = 'overlay-bg-img';
          img.alt = '';
          overlay.insertBefore(img, overlay.firstChild);
          setTimeout(() => {
            img.classList.add('visible');
          }, 10);
        }
      };

      // Priorität: Lottie (inkl. Darkmode-Variante), danach Bild
      if (hotspotConfig.lottie) {
        const lottieContainer = document.createElement('div');
        lottieContainer.className = 'overlay-bg-lottie';
        lottieContainer.id = 'overlay-bg-lottie';
        // iOS Fix: Explizite Größe setzen
        lottieContainer.style.width = '100%';
        lottieContainer.style.height = '100%';
        overlay.insertBefore(lottieContainer, overlay.firstChild);

        // Hell/Dunkel konfigurieren
        let lottieConfig = hotspotConfig.lottie;
        if (container && container.classList.contains('dark') && hotspotConfig.lottieDark) {
          lottieConfig = hotspotConfig.lottieDark;
        }

        // iOS Fix: Kleine Verzögerung, damit Container vollständig im DOM ist
        setTimeout(() => {
          ensureLottieReady().then(lottieLib => {
            if (!lottieLib || !lottieLib.loadAnimation) {
              // Falls Lottie nicht geladen werden konnte, auf Bild-Fallback wechseln
              lottieContainer.remove();
              renderBackgroundImage();
              return;
            }

            const lottieInstance = lottieLib.loadAnimation({
              container: lottieContainer,
              path: lottieConfig.path,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              name: 'Hotspot-' + key + '-Animation',
              rendererSettings: {
                // "slice" sorgt dafür, dass die Animation den gesamten Container füllt
                preserveAspectRatio: 'xMidYMid slice',
                // iOS Fix: SVG-Rendering optimieren
                clearCanvas: true,
                progressiveLoad: false
              }
            });

            // iOS Fix: Animation explizit starten, falls autoplay nicht funktioniert
            if (lottieInstance) {
              lottieInstance.addEventListener('DOMLoaded', () => {
                lottieInstance.play();
              });
              // Fallback: Direkt starten nach kurzer Verzögerung
              setTimeout(() => {
                if (lottieInstance.isPaused) {
                  lottieInstance.play();
                }
              }, 100);
            }

            currentOverlayLottie = lottieInstance;
          });
        }, 10);
      } else {
        renderBackgroundImage();
      }
}

document.querySelectorAll('.hotspot').forEach(hotspot => {
    // Barrierefreiheit: per Tab erreichbar, Tastatur aktivierbar
    const hotspotName = hotspot.getAttribute('data-hotspot') || 'Hotspot';
    hotspot.setAttribute('tabindex', '0');
    hotspot.setAttribute('role', 'button');
    hotspot.setAttribute('aria-label', `${hotspotName} – Mehr erfahren`);
    const iconEl = hotspot.querySelector('.hotspot-icon');
    if (iconEl) iconEl.setAttribute('aria-hidden', 'true');

    // Click Event für Desktop
    hotspot.addEventListener('click', function(e) {
      handleHotspotInteraction(this, e);
    });

    // Tastatur: Enter oder Leertaste aktivieren
    hotspot.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!this.classList.contains('not-active')) {
          handleHotspotInteraction(this, e);
        }
      }
    });
    
    // Touch Events für iOS/Mobile
    let touchStartTime = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    
    hotspot.addEventListener('touchstart', function(e) {
      // Verhindere, dass das Touch-Event an die Drag-Logik weitergegeben wird
      e.stopPropagation();
      const touch = e.touches[0];
      touchStartTime = Date.now();
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: true });
    
    hotspot.addEventListener('touchend', function(e) {
      // Verhindere, dass das Touch-Event an die Drag-Logik weitergegeben wird
      e.stopPropagation();
      
      const touch = e.changedTouches[0];
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - touchStartTime;
      const deltaX = Math.abs(touch.clientX - touchStartX);
      const deltaY = Math.abs(touch.clientY - touchStartY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // Nur als Tap behandeln, wenn:
      // - Die Berührung kurz war (< 300ms)
      // - Die Bewegung klein war (< 10px)
      // - Der Benutzer nicht gerade gedraggt hat
      if (touchDuration < 300 && distance < 10 && !isDragging) {
        e.preventDefault();
        handleHotspotInteraction(this, e);
      }
    });

    // Tooltip-Funktionalität: Name nach 2 Sekunden Hover anzeigen
    let tooltipTimeout = null;
    let tooltipElement = null;

    // Erstelle Tooltip-Element
    tooltipElement = document.createElement('div');
    tooltipElement.className = 'hotspot-tooltip';
    tooltipElement.textContent = hotspotName;
    hotspot.appendChild(tooltipElement);

    hotspot.addEventListener('mouseenter', function() {
      // Starte Timer für 1 Sekunde
      tooltipTimeout = setTimeout(() => {
        if (tooltipElement) {
          tooltipElement.classList.add('visible');
        }
      }, 1000);
    });

    hotspot.addEventListener('mouseleave', function() {
      // Stoppe Timer und verstecke Tooltip
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = null;
      }
      if (tooltipElement) {
        tooltipElement.classList.remove('visible');
      }
    });
  });
  
  
  function closeOverlay() {
    const overlay = document.getElementById('overlay');
    const overlayContent = document.getElementById('overlayContent');
    const graphic = document.querySelector('.graphic-container');
    
    // Alles zusammen ausblenden
    // overlayContent schneller ausfaden (0.2s)
    if (overlayContent) {
      overlayContent.style.transition = 'opacity 0.2s ease-out';
      overlayContent.style.opacity = '0';
    }
    
    // Overlay-Hintergrund ausfaden (0.5s)
    overlay.classList.add('fading-out');
    overlay.style.transition = 'opacity 0.5s ease-out';
    overlay.style.opacity = '0';
    
    // Zoom zurücksetzen mit 0.5s Transition
    graphic.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    graphic.classList.remove('zooming');
    graphic.style.transform = '';
    
    // Transition nach Animation wieder zurücksetzen
    setTimeout(() => {
      graphic.style.transition = '';
    }, 500);
    
    // Nach 0.5s Overlay komplett ausblenden
    setTimeout(() => {
      overlay.classList.remove('active', 'fading-out');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.opacity = '';
      overlay.style.transition = '';
      if (overlayContent) {
        overlayContent.style.display = '';
        overlayContent.style.opacity = '';
        overlayContent.style.transition = '';
      }
    }, 500);
    // Hintergrundbild entfernen
    const oldBg = document.getElementById('overlay-bg-img');
    if (oldBg) oldBg.remove();
    // Lottie-Hintergrund entfernen
    const oldLottie = document.getElementById('overlay-bg-lottie');
    if (oldLottie) oldLottie.remove();
    if (currentOverlayLottie) {
      try {
        currentOverlayLottie.destroy();
      } catch (err) {
        console.error('Fehler beim Zerstören der Lottie-Animation beim Schließen:', err);
      }
      currentOverlayLottie = null;
    }
    // Hintergrundvideo entfernen
    const oldVideo = document.getElementById('overlay-bg-video');
    if (oldVideo) oldVideo.remove();
    // "Zurück zur Übersicht" Button beim Video entfernen
    const backToOverviewBtn = document.querySelector('.back-to-overview-video');
    if (backToOverviewBtn) backToOverviewBtn.remove();
    // Mute/Unmute Button entfernen
    const muteBtn = document.querySelector('.video-mute-btn');
    if (muteBtn) muteBtn.remove();
    // Klasse vom stromnetz-container entfernen
    const stromnetzContainer = document.querySelector('.stromnetz-container');
    if (stromnetzContainer) {
      stromnetzContainer.classList.remove('technische_ansicht-active');
    }
    // overlayContent wieder einblenden
    if (overlayContent) {
      overlayContent.style.display = '';
    }
    // Button-Text zurücksetzen
    const techBtn = document.querySelector('.technische_ansicht-btn');
    if (techBtn && techBtn.getAttribute('data-original-text')) {
      techBtn.textContent = techBtn.getAttribute('data-original-text');
    }
    // .technische_ansicht-container deaktivieren
    const techContainer = document.querySelector('.technische_ansicht-container.active');
    if (techContainer) techContainer.classList.remove('active');

    // Aktuellen Hotspot-Status zurücksetzen
    currentOverlayHotspotKey = null;
  }
  
  // Event Delegation für Zurück-Link im Overlay
  // Da der Link dynamisch eingefügt wird, nutze Event Delegation
  document.getElementById('overlayContent').addEventListener('click', function(e) {
    const link = e.target.closest('a.back-to-overview');
    if (link) {
      e.preventDefault();
      closeOverlay();
    }
  });
  
  // Dark Mode Switch
  const darkmodeToggle = document.getElementById('darkmodeToggle');
  const toggleIcon = document.getElementById('toggleIcon');
  
  // Dark Mode Info-Overlay Event-Listener
  const darkmodeInfoOverlay = document.getElementById('darkmodeInfoOverlay');
  const darkmodeInfoClose = document.getElementById('darkmodeInfoClose');
  const darkmodeInfoButton = document.getElementById('darkmodeInfoButton');
  
  function closeDarkmodeInfo() {
    if (darkmodeInfoOverlay) {
      darkmodeInfoOverlay.classList.remove('visible');
      darkmodeInfoOverlay.setAttribute('aria-hidden', 'true');
      localStorage.setItem('darkmodeInfoShown', '1');
    }
  }
  
  if (darkmodeInfoClose) {
    darkmodeInfoClose.addEventListener('click', closeDarkmodeInfo);
  }
  
  if (darkmodeInfoButton) {
    darkmodeInfoButton.addEventListener('click', closeDarkmodeInfo);
  }
  
  // Schließen beim Klick außerhalb des Modals
  if (darkmodeInfoOverlay) {
    darkmodeInfoOverlay.addEventListener('click', function(e) {
      if (e.target === darkmodeInfoOverlay) {
        closeDarkmodeInfo();
      }
    });
  }
  
  function toggleDarkmode() {
    const container = document.querySelector('.stromnetz-container');
    container.classList.toggle('dark');
    const isDark = container.classList.contains('dark');
    if (darkmodeToggle) {
      darkmodeToggle.setAttribute('aria-checked', isDark ? 'true' : 'false');
    }
    
    // Hintergrund-Lottie-Animation wechseln (nur Sichtbarkeit, keine Neustart)
    switchBackgroundLottie(isDark);
    
    if(isDark) {
      // Dark Mode Info-Overlay anzeigen (nur beim ersten Aktivieren)
      const darkmodeInfoShown = localStorage.getItem('darkmodeInfoShown');
      if (!darkmodeInfoShown) {
        const darkmodeInfoOverlay = document.getElementById('darkmodeInfoOverlay');
        if (darkmodeInfoOverlay) {
          darkmodeInfoOverlay.classList.add('visible');
          darkmodeInfoOverlay.setAttribute('aria-hidden', 'false');
        }
      }
    }

    // Overlay-Bild / -Lottie ggf. wechseln und faden
    const overlay = document.getElementById('overlay');
    const overlayImg = document.getElementById('overlay-bg-img');
    if (overlay.classList.contains('active')) {
      // Aktuellen Hotspot-Key möglichst direkt verwenden
      let key = currentOverlayHotspotKey || null;

      if (!key) {
        // Fallback: anhand des HTML-Inhalts bestimmen (Alt-Verhalten)
        const overlayContent = document.getElementById('overlayContent');
        for (const k in hotspotContents) {
          if (hotspotContents[k].html && overlayContent.innerHTML.includes(hotspotContents[k].html.trim().slice(0, 30))) {
            key = k;
            break;
          }
        }
      }

      if (key && hotspotContents[key]) {
        // Bild-Hintergrund aktualisieren (falls vorhanden)
        if (overlayImg) {
          overlayImg.classList.remove('visible');
          setTimeout(() => {
            let newImg = hotspotContents[key].img;
            if (isDark && hotspotContents[key].imgDark) {
              newImg = hotspotContents[key].imgDark;
            }
            if (newImg) {
              overlayImg.src = newImg;
            }
            setTimeout(() => {
              overlayImg.classList.add('visible');
            }, 20);
          }, 250); // Dauer wie in CSS-Transition
        }

        // Lottie-Hintergrund aktualisieren (falls vorhanden)
        let overlayLottieContainer = document.getElementById('overlay-bg-lottie');
        // Falls Lottie initial fehlte, Container nachträglich anlegen
        if (!overlayLottieContainer && (hotspotContents[key].lottie || hotspotContents[key].lottieDark)) {
          overlayLottieContainer = document.createElement('div');
          overlayLottieContainer.className = 'overlay-bg-lottie';
          overlayLottieContainer.id = 'overlay-bg-lottie';
          overlayLottieContainer.style.width = '100%';
          overlayLottieContainer.style.height = '100%';
          overlay.insertBefore(overlayLottieContainer, overlay.firstChild);
        }

        if (overlayLottieContainer && (hotspotContents[key].lottie || hotspotContents[key].lottieDark)) {
          // alte Instanz zerstören
          if (currentOverlayLottie) {
            try {
              currentOverlayLottie.destroy();
            } catch (err) {
              console.error('Fehler beim Zerstören der Lottie-Animation beim Darkmode-Wechsel:', err);
            }
            currentOverlayLottie = null;
          }

          let lottieConfig = hotspotContents[key].lottie;
          if (isDark && hotspotContents[key].lottieDark) {
            lottieConfig = hotspotContents[key].lottieDark;
          }

          if (lottieConfig) {
            // iOS Fix: Kleine Verzögerung für bessere Kompatibilität
            setTimeout(() => {
              ensureLottieReady().then(lottieLib => {
                if (!lottieLib || !lottieLib.loadAnimation) {
                  return;
                }

                const lottieInstance = lottieLib.loadAnimation({
                  container: overlayLottieContainer,
                  path: lottieConfig.path,
                  renderer: 'svg',
                  loop: true,
                  autoplay: true,
                  name: 'Hotspot-' + key + '-Animation',
                  rendererSettings: {
                    preserveAspectRatio: 'xMidYMid slice',
                    // iOS Fix: SVG-Rendering optimieren
                    clearCanvas: true,
                    progressiveLoad: false
                  }
                });

                // iOS Fix: Animation explizit starten, falls autoplay nicht funktioniert
                if (lottieInstance) {
                  lottieInstance.addEventListener('DOMLoaded', () => {
                    lottieInstance.play();
                  });
                  // Fallback: Direkt starten nach kurzer Verzögerung
                  setTimeout(() => {
                    if (lottieInstance.isPaused) {
                      lottieInstance.play();
                    }
                  }, 100);
                }

                currentOverlayLottie = lottieInstance;
              });
            }, 10);
          }
        }
      }
      
      // Video-Hintergrund in technischer Ansicht aktualisieren (falls vorhanden)
      const techVideo = document.getElementById('overlay-bg-video');
      if (techVideo) {
        const source16x9 = document.getElementById('techVideoSource16x9');
        const source9x16 = document.getElementById('techVideoSource9x16');
        
        if (source16x9 && source9x16) {
          const videoType = isDark ? 'Nacht' : 'Tag';
          const currentSrc = techVideo.src;
          const isMobile = window.innerWidth <= 768;
          const wasPlaying = !techVideo.paused;
          const currentTime = techVideo.currentTime;
          
          // Video-Quellen aktualisieren
          source16x9.src = `assets/video/251107_Master-Scene_${videoType}_sound_16x9.mp4`;
          source9x16.src = `assets/video/251107_Master-Scene_${videoType}_sound_9x16.mp4`;
          
          // Richtige Quelle basierend auf Bildschirmgröße setzen
          if (isMobile) {
            techVideo.src = source9x16.src;
          } else {
            techVideo.src = source16x9.src;
          }
          
          // Video neu laden
          techVideo.load();
          
          // Video an der gleichen Stelle fortsetzen, wenn es vorher lief
          if (wasPlaying) {
            techVideo.addEventListener('loadeddata', function() {
              techVideo.currentTime = currentTime;
              const playPromise = techVideo.play();
              if (playPromise !== undefined) {
                playPromise.catch(error => {
                  console.log('Video konnte nicht automatisch abgespielt werden:', error);
                });
              }
            }, { once: true });
          }
        }
      }
    }
  };

  if (darkmodeToggle) {
    darkmodeToggle.addEventListener('click', toggleDarkmode);
    darkmodeToggle.addEventListener('keydown', function(e) {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        toggleDarkmode();
      }
    });
    const container = document.querySelector('.stromnetz-container');
    if (container && container.classList.contains('dark')) {
      darkmodeToggle.setAttribute('aria-checked', 'true');
    }
  }

  // Drag-Funktionalität für background-img (jetzt Lottie-Animation)
  const backgroundImg = document.getElementById('background-lottie');
  const backgroundImgWrap = document.querySelector('.background-img_wrap');
  const graphicContainer = document.querySelector('.graphic-container');
  let startX = 0;
  let startY = 0;
  let currentX = 0;
  let currentY = 0;
  let translateX = 0;
  let translateY = 0;
  let canDragX = false; // Kann horizontal gedraggt werden
  let canDragY = false; // Kann vertikal gedraggt werden
  let hasUserDragged = false; // Hat der Nutzer bereits aktiv verschoben?

  // Hilfsfunktion zum Abrufen der Größe der Lottie-Animation
  // Verwendet die native Größe aus der Lottie-Animation (2028x1800)
  // Verwendet die sichtbare Animation (Tag oder Dark)
  function getLottieDimensions() {
    const activeLottie = currentBackgroundLottie || currentBackgroundLottieDark;
    
    if (!activeLottie) {
      // Fallback: Verwende die native Größe der Animation (aus data.json)
      return { width: 2028, height: 1800 };
    }
    
    // Versuche die native Größe aus der Lottie-Animation zu bekommen
    try {
      // Lottie-Animationen haben eine 'renderer' Property mit der SVG-Größe
      const renderer = activeLottie.renderer;
      if (renderer && renderer.svgElement) {
        const svg = renderer.svgElement;
        const viewBox = svg.getAttribute('viewBox');
        if (viewBox) {
          const [x, y, width, height] = viewBox.split(' ').map(Number);
          if (width && height) {
            return { width, height };
          }
        }
      }
      
      // Fallback: Verwende die native Größe aus der Animation-Konfiguration
      if (activeLottie.animationData) {
        const w = activeLottie.animationData.w;
        const h = activeLottie.animationData.h;
        if (w && h) {
          return { width: w, height: h };
        }
      }
    } catch (err) {
      console.log('Fehler beim Abrufen der Lottie-Größe:', err);
    }
    
    // Fallback: Native Größe der Animation (2028x1800)
    return { width: 2028, height: 1800 };
  }

  // Funktion zur Berechnung, ob das Bild/Lottie abgeschnitten wird
  function checkIfImageIsCropped() {
    if (!backgroundImg) {
      console.log('[DEBUG] checkIfImageIsCropped: backgroundImg (Lottie) nicht gefunden');
      return;
    }
    
    // Für Lottie-Animationen: Größe aus dem Container bestimmen
    const { width: naturalWidth, height: naturalHeight } = getLottieDimensions();
    
    console.log('[DEBUG] checkIfImageIsCropped aufgerufen:', {
      naturalWidth: naturalWidth,
      naturalHeight: naturalHeight
    });
    
    if (!naturalWidth || !naturalHeight || naturalWidth === 0 || naturalHeight === 0) {
      // Lottie noch nicht geladen, versuche es später nochmal
      console.log('[DEBUG] Lottie noch nicht geladen, versuche es später');
      setTimeout(checkIfImageIsCropped, 100);
      return;
    }
    
    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight;
    
    // Berechne das Seitenverhältnis von Bild und Container
    const imageAspect = naturalWidth / naturalHeight;
    const containerAspect = containerWidth / containerHeight;
    
    console.log('[DEBUG] Dimensionen und Seitenverhältnisse:', {
      containerWidth: containerWidth,
      containerHeight: containerHeight,
      imageAspect: imageAspect,
      containerAspect: containerAspect
    });
    
    // Bei object-fit: cover wird das Bild so skaliert, dass es den Container vollständig ausfüllt
    // Wenn imageAspect > containerAspect: Bild ist breiter → wird oben/unten abgeschnitten → vertikales Dragging
    // Wenn imageAspect < containerAspect: Bild ist höher → wird links/rechts abgeschnitten → horizontales Dragging
    
    // Berechne die tatsächliche skalierte Größe des Bildes
    let scaledWidth, scaledHeight;
    
    if (imageAspect > containerAspect) {
      // Bild ist breiter → wird so skaliert, dass es die Höhe ausfüllt, Breite wird abgeschnitten
      scaledHeight = containerHeight;
      scaledWidth = containerHeight * imageAspect;
    } else {
      // Bild ist höher → wird so skaliert, dass es die Breite ausfüllt, Höhe wird abgeschnitten
      scaledWidth = containerWidth;
      scaledHeight = containerWidth / imageAspect;
    }
    
    console.log('[DEBUG] Skalierte Dimensionen:', {
      scaledWidth: scaledWidth,
      scaledHeight: scaledHeight,
      containerWidth: containerWidth,
      containerHeight: containerHeight
    });
    
    // Mobile: Immer in beide Richtungen draggbar
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Mobile: Stadtgrafik ist größer (250vw x 250vh), daher immer in beide Richtungen draggbar
      canDragX = true;
      canDragY = true;
      // Für mobile: Wrapper-Größe basierend auf tatsächlicher Bildgröße berechnen
      // Verwende das Bildverhältnis, aber mache es größer als den Container
      if (naturalWidth && naturalHeight) {
        const imageAspect = naturalWidth / naturalHeight;
        // Skaliere so, dass das Bild 2x größer ist als der Container
        // Aber behalte das Seitenverhältnis bei
        if (imageAspect > containerAspect) {
          // Bild ist breiter - skaliere nach Höhe
          scaledHeight = containerHeight * 2;
          scaledWidth = scaledHeight * imageAspect;
        } else {
          // Bild ist höher - skaliere nach Breite
          scaledWidth = containerWidth * 2;
          scaledHeight = scaledWidth / imageAspect;
        }
      } else {
        // Fallback falls Bild noch nicht geladen
        scaledWidth = containerWidth * 2;
        scaledHeight = containerHeight * 2;
      }
      console.log('[DEBUG] Mobile: Dragging in beide Richtungen aktiviert', {
        scaledWidth,
        scaledHeight,
        naturalWidth,
        naturalHeight
      });
    } else {
      // Desktop: Stadtgrafik ist 1.25x größer, daher immer in beide Richtungen draggbar
      canDragX = true;
      canDragY = true;
      // Für Desktop: Wrapper-Größe basierend auf tatsächlicher Bildgröße berechnen
      // Verwende das Bildverhältnis, aber mache es 1.25x größer als den Container
      if (naturalWidth && naturalHeight) {
        const imageAspect = naturalWidth / naturalHeight;
        // Skaliere so, dass das Bild 1.25x größer ist als der Container
        // Aber behalte das Seitenverhältnis bei
        if (imageAspect > containerAspect) {
          // Bild ist breiter - skaliere nach Höhe
          scaledHeight = containerHeight * 1.25;
          scaledWidth = scaledHeight * imageAspect;
        } else {
          // Bild ist höher - skaliere nach Breite
          scaledWidth = containerWidth * 1.25;
          scaledHeight = scaledWidth / imageAspect;
        }
      } else {
        // Fallback falls Bild noch nicht geladen
        scaledWidth = containerWidth * 1.25;
        scaledHeight = containerHeight * 1.25;
      }
      console.log('[DEBUG] Desktop: Dragging in beide Richtungen aktiviert (1.25x)', {
        scaledWidth,
        scaledHeight,
        naturalWidth,
        naturalHeight
      });
    }
    
    console.log('[DEBUG] Finale Werte:', {
      canDragX: canDragX,
      canDragY: canDragY,
      isMobile: isMobile
    });
    
    // Cursor entsprechend anpassen
    if (backgroundImgWrap) {
      if (canDragX || canDragY) {
        backgroundImgWrap.style.cursor = 'grab';
        console.log('[DEBUG] Cursor auf "grab" gesetzt');
      } else {
        backgroundImgWrap.style.cursor = 'default';
        console.log('[DEBUG] Cursor auf "default" gesetzt');
      }
      
      // Setze die Wrapper-Größe auf die tatsächliche skalierte Bildgröße
      // damit das Bild richtig verschoben werden kann
      // Für mobile (2.5x) und desktop (1.25x) wird die Größe bereits oben berechnet (basierend auf Bildverhältnis)
      backgroundImgWrap.style.width = scaledWidth + 'px';
      backgroundImgWrap.style.height = scaledHeight + 'px';
      console.log('[DEBUG] Wrapper-Größe gesetzt:', {
        width: scaledWidth + 'px',
        height: scaledHeight + 'px'
      });

      // Standardposition des Bildes zentrieren, solange der Nutzer noch nicht aktiv gedragt hat
      if (!hasUserDragged) {
        let offsetX = 0;
        let offsetY = 0;

        if (canDragX && scaledWidth > containerWidth) {
          // horizontal abgeschnitten → mittig ausrichten
          offsetX = (containerWidth - scaledWidth) / 2;
        }
        if (canDragY && scaledHeight > containerHeight) {
          // vertikal abgeschnitten → mittig ausrichten
          offsetY = (containerHeight - scaledHeight) / 2;
        }

        translateX = currentX = offsetX;
        translateY = currentY = offsetY;

        console.log('[DEBUG] Initiale Zentrierung gesetzt:', {
          offsetX,
          offsetY,
          scaledWidth,
          scaledHeight,
          containerWidth,
          containerHeight,
          isMobile
        });
      } else {
        // Wenn der Nutzer bereits gedragt hat, aktuelle Position als Ausgangspunkt verwenden
        currentX = translateX;
        currentY = translateY;
      }

      // In jedem Fall sicherstellen, dass das Bild nach Resize weiterhin vollflächig bleibt
      // Begrenzungen für mobile und desktop
      if (canDragX && scaledWidth > containerWidth) {
        const minX = containerWidth - scaledWidth; // ganz nach rechts gezogen (negativer Wert)
        const maxX = 0;                             // ganz nach links (Startposition)
        // Sicherstellen, dass currentX innerhalb der Grenzen liegt
        currentX = Math.max(minX, Math.min(maxX, currentX));
        console.log('[DEBUG] X-Begrenzung:', { minX, maxX, currentX, scaledWidth, containerWidth });
      } else if (!isMobile) {
        // Nur für Desktop: Wenn kein horizontales Dragging, auf 0 setzen
        currentX = 0;
      }

      if (canDragY && scaledHeight > containerHeight) {
        const minY = containerHeight - scaledHeight; // ganz nach unten gezogen (negativer Wert)
        const maxY = 0;                               // ganz nach oben (Startposition)
        // Sicherstellen, dass currentY innerhalb der Grenzen liegt
        currentY = Math.max(minY, Math.min(maxY, currentY));
        console.log('[DEBUG] Y-Begrenzung:', { minY, maxY, currentY, scaledHeight, containerHeight });
      } else if (!isMobile) {
        // Nur für Desktop: Wenn kein vertikales Dragging, auf 0 setzen
        currentY = 0;
      }

      translateX = currentX;
      translateY = currentY;
      backgroundImgWrap.style.transform = `translate(${currentX}px, ${currentY}px)`;
      console.log('[DEBUG] Position nach Resize / Zentrierung gesetzt:', {
        currentX,
        currentY
      });
    }
  }

  // Prüfe beim Laden der Lottie-Animation
  if (backgroundImg && backgroundImgWrap) {
    // Versuche sofort zu prüfen
    checkIfImageIsCropped();
    
    // Warte auf Lottie-Animation geladen (wird in loadBackgroundLottie gesetzt)
    // Prüfe mit Delays, damit die Animation Zeit zum Laden hat
    setTimeout(checkIfImageIsCropped, 100);
    setTimeout(checkIfImageIsCropped, 500);
    setTimeout(checkIfImageIsCropped, 1000);
    
    // Prüfe auch bei Fenstergrößenänderung
    window.addEventListener('resize', checkIfImageIsCropped);
  }

  if (backgroundImg && backgroundImgWrap && graphicContainer) {
    // Mousedown Event
    backgroundImgWrap.addEventListener('mousedown', function(e) {
      console.log('[DEBUG] mousedown Event auf backgroundImg');
      
      // Verhindere Drag, wenn Overlay aktiv ist
      const overlay = document.getElementById('overlay');
      if (overlay && overlay.classList.contains('active')) {
        console.log('[DEBUG] Overlay ist aktiv, Drag verhindert');
        return;
      }
      
      // Prüfe erneut, ob Dragging möglich ist
      const { width: naturalWidth, height: naturalHeight } = getLottieDimensions();

      console.log('[DEBUG] mousedown - natürliche Dimensionen:', {
        naturalWidth: naturalWidth,
        naturalHeight: naturalHeight
      });

      if (naturalWidth && naturalHeight && naturalWidth > 0 && naturalHeight > 0) {
        checkIfImageIsCropped();
      } else {
        // Falls Dimensionen noch nicht verfügbar sind, versuche es trotzdem
        console.log('[DEBUG] Dimensionen nicht verfügbar, versuche trotzdem checkIfImageIsCropped');
        checkIfImageIsCropped();
      }
      
      console.log('[DEBUG] mousedown - canDragX:', canDragX, 'canDragY:', canDragY);
      
      // Verhindere Drag, wenn in dieser Richtung nicht möglich
      if (!canDragX && !canDragY) {
        console.log('[DEBUG] Drag nicht möglich, beende Event');
        return;
      }
      
      console.log('[DEBUG] Starte Drag');
      isDragging = true;
      backgroundImgWrap.classList.add('dragging');
      hasUserDragged = true;
      
      startX = e.clientX;
      startY = e.clientY;
      
      // Aktuelle Transform-Werte des Wrappers auslesen
      const style = window.getComputedStyle(backgroundImgWrap);
      const transform = style.transform;
      if (transform && transform !== 'none') {
        const matrix = new DOMMatrix(transform);
        translateX = matrix.e;
        translateY = matrix.f;
      } else {
        translateX = 0;
        translateY = 0;
      }
      
      console.log('[DEBUG] Drag gestartet:', {
        startX: startX,
        startY: startY,
        translateX: translateX,
        translateY: translateY
      });
      
      e.preventDefault();
    });

    // Mousemove Event
    document.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      // Nur in erlaubten Richtungen bewegen
      if (canDragX) {
        currentX = translateX + deltaX;
      } else {
        currentX = translateX;
      }
      
      if (canDragY) {
        currentY = translateY + deltaY;
      } else {
        currentY = translateY;
      }
      
      // Begrenze die Bewegung basierend auf Bildgröße
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;
      const isMobile = window.innerWidth <= 768;

      let actualImgWidth, actualImgHeight;
      const { width: naturalWidth, height: naturalHeight } = getLottieDimensions();
      
      if (naturalWidth && naturalHeight) {
        const imageAspect = naturalWidth / naturalHeight;
        const containerAspect = containerWidth / containerHeight;
        
        if (isMobile) {
          // Mobile: Skaliere auf 2.5x Container-Größe, behalte aber Seitenverhältnis
          if (imageAspect > containerAspect) {
            actualImgHeight = containerHeight * 2.5;
            actualImgWidth = actualImgHeight * imageAspect;
          } else {
            actualImgWidth = containerWidth * 2.5;
            actualImgHeight = actualImgWidth / imageAspect;
          }
        } else {
          // Desktop: Skaliere auf 1.25x Container-Größe, behalte aber Seitenverhältnis
          if (imageAspect > containerAspect) {
            actualImgHeight = containerHeight * 1.25;
            actualImgWidth = actualImgHeight * imageAspect;
          } else {
            actualImgWidth = containerWidth * 1.25;
            actualImgHeight = actualImgWidth / imageAspect;
          }
        }
      } else {
        // Fallback falls Bild noch nicht geladen
        if (isMobile) {
          actualImgWidth = containerWidth * 2.5;
          actualImgHeight = containerHeight * 2.5;
        } else {
          actualImgWidth = containerWidth * 1.25;
          actualImgHeight = containerHeight * 1.25;
        }
      }
      
      // Berechne maximale Verschiebung nur in erlaubten Richtungen
      // Da der Wrapper links bei 0 startet und nach links verschoben wird,
      // liegen die Grenzen bei [containerWidth - actualImgWidth, 0] bzw. [containerHeight - actualImgHeight, 0].
      if (canDragX) {
        const minX = containerWidth - actualImgWidth; // ganz nach rechts gezogen
        const maxX = 0;                               // ganz nach links (Startposition)
        currentX = Math.max(minX, Math.min(maxX, currentX));
      }
      
      if (canDragY) {
        const minY = containerHeight - actualImgHeight;
        const maxY = 0;
        currentY = Math.max(minY, Math.min(maxY, currentY));
      }
      
      // Wende die Transformation auf den Wrapper an (damit Bild und Hotspots zusammen verschoben werden)
      backgroundImgWrap.style.transform = `translate(${currentX}px, ${currentY}px)`;
      
      e.preventDefault();
    });

    // Mouseup Event
    document.addEventListener('mouseup', function(e) {
      if (isDragging) {
        isDragging = false;
        backgroundImgWrap.classList.remove('dragging');
        
        translateX = currentX;
        translateY = currentY;
      }
    });

    // Touch-Events für mobile Geräte
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    let hasMoved = false;
    
    backgroundImgWrap.addEventListener('touchstart', function(e) {
      // Ignoriere Touch-Events, die von Hotspots kommen
      if (e.target.closest('.hotspot')) {
        return;
      }
      
      const overlay = document.getElementById('overlay');
      if (overlay && overlay.classList.contains('active')) {
        return;
      }
      
      // Prüfe erneut, ob Dragging möglich ist
      checkIfImageIsCropped();
      
      // Verhindere Drag, wenn in dieser Richtung nicht möglich
      if (!canDragX && !canDragY) {
        return;
      }
      
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartTime = Date.now();
      hasMoved = false;
      
      // Aktuelle Transform-Werte des Wrappers auslesen
      const style = window.getComputedStyle(backgroundImgWrap);
      const transform = style.transform;
      if (transform && transform !== 'none') {
        const matrix = new DOMMatrix(transform);
        translateX = matrix.e;
        translateY = matrix.f;
      } else {
        translateX = 0;
        translateY = 0;
      }
      
      // Verhindere Pull-to-Refresh, wenn auf Stadtgrafik gedraggt wird
      e.stopPropagation();
    }, { passive: false });

    document.addEventListener('touchmove', function(e) {
      // Ignoriere Touch-Events, die von Hotspots kommen
      if (e.target.closest('.hotspot')) {
        return;
      }
      
      // Prüfe, ob der Touch auf der Stadtgrafik ist
      const isOnCity = e.target.closest('.background-img_wrap') || 
                       e.target.closest('.graphic-container') ||
                       e.target.classList.contains('background-img') ||
                       e.target.classList.contains('background-img_wrap');
      
      if (!isOnCity) {
        return; // Nicht auf Stadtgrafik, lass andere Handler das Event behandeln
      }
      
      if (!isDragging) {
        // Prüfe, ob es sich um eine Bewegung handelt (nicht nur ein Tap)
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartX);
        const deltaY = Math.abs(touch.clientY - touchStartY);
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        // Wenn die Bewegung größer als 5px ist, starte das Dragging
        if (distance > 5) {
          hasMoved = true;
          isDragging = true;
          backgroundImgWrap.classList.add('dragging');
          hasUserDragged = true;
          startX = touchStartX;
          startY = touchStartY;
        } else {
          return;
        }
      }
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      
      // Nur in erlaubten Richtungen bewegen
      if (canDragX) {
        currentX = translateX + deltaX;
      } else {
        currentX = translateX;
      }
      
      if (canDragY) {
        currentY = translateY + deltaY;
      } else {
        currentY = translateY;
      }
      
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;
      const isMobile = window.innerWidth <= 768;

      let actualImgWidth, actualImgHeight;
      const { width: naturalWidth, height: naturalHeight } = getLottieDimensions();
      
      if (naturalWidth && naturalHeight) {
        const imageAspect = naturalWidth / naturalHeight;
        const containerAspect = containerWidth / containerHeight;
        
        if (isMobile) {
          // Mobile: Skaliere auf 2.5x Container-Größe, behalte aber Seitenverhältnis
          if (imageAspect > containerAspect) {
            actualImgHeight = containerHeight * 2.5;
            actualImgWidth = actualImgHeight * imageAspect;
          } else {
            actualImgWidth = containerWidth * 2.5;
            actualImgHeight = actualImgWidth / imageAspect;
          }
        } else {
          // Desktop: Skaliere auf 1.25x Container-Größe, behalte aber Seitenverhältnis
          if (imageAspect > containerAspect) {
            actualImgHeight = containerHeight * 1.25;
            actualImgWidth = actualImgHeight * imageAspect;
          } else {
            actualImgWidth = containerWidth * 1.25;
            actualImgHeight = actualImgWidth / imageAspect;
          }
        }
      } else {
        // Fallback falls Bild noch nicht geladen
        if (isMobile) {
          actualImgWidth = containerWidth * 2.5;
          actualImgHeight = containerHeight * 2.5;
        } else {
          actualImgWidth = containerWidth * 1.25;
          actualImgHeight = containerHeight * 1.25;
        }
      }
      
      // Begrenze die Verschiebung symmetrisch wie bei der Maus:
      if (canDragX) {
        const minX = containerWidth - actualImgWidth;
        const maxX = 0;
        currentX = Math.max(minX, Math.min(maxX, currentX));
      }
      
      if (canDragY) {
        const minY = containerHeight - actualImgHeight;
        const maxY = 0;
        currentY = Math.max(minY, Math.min(maxY, currentY));
      }
      
      // Wende die Transformation auf den Wrapper an (damit Bild und Hotspots zusammen verschoben werden)
      backgroundImgWrap.style.transform = `translate(${currentX}px, ${currentY}px)`;
      
      // WICHTIG: Verhindere Pull-to-Refresh und stoppe Event-Propagation
      e.preventDefault();
      e.stopPropagation();
    }, { passive: false });

    document.addEventListener('touchend', function(e) {
      // Ignoriere Touch-Events, die von Hotspots kommen
      if (e.target.closest('.hotspot')) {
        return;
      }
      
      if (isDragging) {
        isDragging = false;
        backgroundImgWrap.classList.remove('dragging');
        
        translateX = currentX;
        translateY = currentY;
      }
      
      // Reset für nächsten Touch
      hasMoved = false;
    });
  }

  // NEU: Overlay-Bild als Schließen-Trigger
  document.addEventListener('click', function(e) {
    if (e.target && e.target.id === 'overlay-bg-img') {
      // closeOverlay entfernt
    }
  });

  // Display-Button und Mobile Filter-Container
  const displayBtn = document.getElementById('stromtypDisplayBtn');
  const mobileFilterContainer = document.querySelector('.stromtyp-filter-container.mobile-only');

  // Funktion zum Aktualisieren des Display-Button-Texts und ARIA-Attribute
  function updateStromtypDisplayText() {
    const displayText = displayBtn?.querySelector('.stromtyp-display-text');
    const filterContainer = document.querySelector('.stromtyp-filter');
    const activeBtn = filterContainer?.querySelector('.filter-btn.active');
    
    if (displayText && activeBtn) {
      displayText.textContent = activeBtn.textContent.trim();
    }
    if (displayBtn) {
      const currentText = displayText?.textContent?.trim() || 'Alle';
      displayBtn.setAttribute('aria-label', `Spannungstyp auswählen, aktuell: ${currentText}`);
    }
  }

  // Filterfunktion für Stromtypen
  const filterContainers = document.querySelectorAll('.stromtyp-filter');
  filterContainers.forEach(filterContainer => {
    if (filterContainer) {
      filterContainer.addEventListener('click', function(e) {
        if (e.target.classList.contains('filter-btn')) {
          const stromtyp = e.target.getAttribute('data-stromtyp');
          
          // Aktiven Button in allen Containern setzen und ARIA aktualisieren
          filterContainers.forEach(container => {
            container.querySelectorAll('.filter-btn').forEach(btn => {
              const isActive = btn.getAttribute('data-stromtyp') === stromtyp;
              btn.classList.toggle('active', isActive);
              btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
            });
          });
          
          // Hotspots filtern
          document.querySelectorAll('.hotspot').forEach(hotspot => {
            const hotspotStromtyp = hotspot.getAttribute('data-stromtyp');
            // Unterstützung für mehrere Stromtypen (durch Leerzeichen getrennt)
            const hotspotStromtypen = hotspotStromtyp ? hotspotStromtyp.split(' ') : [];
            const isMatch = stromtyp === 'alle' || 
                          hotspotStromtyp === stromtyp || 
                          hotspotStromtyp === 'alle-typen' ||
                          hotspotStromtypen.includes(stromtyp);
            if (isMatch) {
              hotspot.classList.add('active');
              hotspot.classList.remove('not-active');
              hotspot.setAttribute('tabindex', '0');
            } else {
              hotspot.classList.remove('active');
              hotspot.classList.add('not-active');
              hotspot.setAttribute('tabindex', '-1');
            }
          });
          
          // Display-Button-Text aktualisieren
          updateStromtypDisplayText();
          
          // Mobile Filter-Liste schließen
          if (mobileFilterContainer) {
            mobileFilterContainer.classList.remove('visible');
          }
          
          // Active-Klasse und ARIA vom Display-Button entfernen
          if (displayBtn) {
            displayBtn.classList.remove('active');
            displayBtn.setAttribute('aria-expanded', 'false');
          }
        }
      });
    }
  });

  // Initialen Text beim Laden setzen
  updateStromtypDisplayText();

  // Display-Button: Filter-Liste öffnen/schließen
  if (displayBtn && mobileFilterContainer) {
    displayBtn.addEventListener('click', function() {
      const isExpanded = mobileFilterContainer.classList.toggle('visible');
      displayBtn.classList.toggle('active', isExpanded);
      displayBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  // STARTSCREEN-LOGIK
  window.addEventListener('DOMContentLoaded', function() {
    const startOverlay = document.getElementById('startscreenOverlay');
    const startBtn = document.getElementById('startJourneyBtn');
    const skipIntroBtn = document.getElementById('skipIntroBtn');
    const graphic = document.querySelector('.graphic-container');
    const introVideo = document.getElementById('introVideo');
    const startscreenContent = document.querySelector('.startscreen-content');
    const startscreenBg = document.querySelector('.startscreen-bg');
    const dragInfoOverlay = document.getElementById('dragInfoOverlay');
    const dragInfoCloseBtn = document.getElementById('dragInfoCloseBtn');

    // Hilfsfunktion zum Ausblenden des Drag-Info-Overlays
    function hideDragInfoOverlay() {
      if (dragInfoOverlay) {
        dragInfoOverlay.classList.remove('visible');
        sessionStorage.setItem('dragInfoShown', '1');
      }
    }
    
    // Funktion zum Ausblenden des Startscreens nach Video-Ende
    function finishIntro() {
      // Skip-Button ausblenden
      if (skipIntroBtn) {
        skipIntroBtn.classList.remove('visible');
      }
      // Intro-Video Volume-Button ausblenden
      const introMuteBtn = document.querySelector('.intro-video-mute-btn');
      if (introMuteBtn) {
        introMuteBtn.classList.remove('visible');
      }
      // Wieder herauszoomen und Startscreen ausblenden
      graphic.style.transform = '';
      setTimeout(() => {
        graphic.classList.remove('zooming');
        startOverlay.classList.remove('zooming');
        startOverlay.classList.add('hidden');
        setTimeout(() => {
          startOverlay.style.display = 'none';
          document.body.style.overflow = '';
          // Drag-Info-Overlay mit Verzögerung anzeigen (nur einmal pro Session)
          if (!sessionStorage.getItem('dragInfoShown') && dragInfoOverlay) {
            setTimeout(() => {
              // Nur anzeigen, wenn es nicht bereits versteckt wurde (z.B. durch frühe Interaktion)
              if (!sessionStorage.getItem('dragInfoShown')) {
                dragInfoOverlay.classList.add('visible');
              }
            }, 3000);
          }
        }, 500);
      }, 100);
    }
    
    // Funktion zum Setzen des richtigen Videos basierend auf Bildschirmgröße
    function setVideoSource() {
      if (!introVideo) return;
      
      const isMobile = window.innerWidth <= 768;
      const videoSource16x9 = document.getElementById('videoSource16x9');
      const videoSource9x16 = document.getElementById('videoSource9x16');
      
      if (isMobile && videoSource9x16) {
        // Mobile: 9x16 Video verwenden
        if (introVideo.src !== videoSource9x16.src) {
          introVideo.src = videoSource9x16.src;
          introVideo.load();
        }
      } else if (!isMobile && videoSource16x9) {
        // Desktop: 16x9 Video verwenden
        if (introVideo.src !== videoSource16x9.src) {
          introVideo.src = videoSource16x9.src;
          introVideo.load();
        }
      }
    }
    
    // Video-Quelle beim Laden setzen
    setVideoSource();
    
    // Video-Quelle bei Fenstergrößenänderung aktualisieren
    window.addEventListener('resize', function() {
      setVideoSource();
    });
    
    // Untertitel: nur vorhandenes HTML-Track nutzen (kein zweites anlegen)
    if (introVideo) {
      dedupeVideoTracks(introVideo);
      enhanceExistingVttTrack(introVideo);
      
      introVideo.addEventListener('loadedmetadata', function() {
        // Untertitel explizit aktivieren
        if (introVideo.textTracks && introVideo.textTracks.length > 0) {
          for (let i = 0; i < introVideo.textTracks.length; i++) {
            introVideo.textTracks[i].mode = 'showing';
          }
        }
      });
      
      // Fallback: Auch beim canplay Event aktivieren
      introVideo.addEventListener('canplay', function() {
        if (introVideo.textTracks && introVideo.textTracks.length > 0) {
          for (let i = 0; i < introVideo.textTracks.length; i++) {
            if (introVideo.textTracks[i].mode !== 'showing') {
              introVideo.textTracks[i].mode = 'showing';
            }
          }
        }
      });
    }
    
    // Nur einmal pro Session anzeigen
    if (!sessionStorage.getItem('stromnetzStarted')) {
      startOverlay.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    } else {
      startOverlay.style.display = 'none';
    }
    
    // Hintergrund-Lottie-Animationen initialisieren (beide parallel laden)
    loadBackgroundLotties();
    
    // Initiale Sichtbarkeit basierend auf Dark Mode setzen
    const container = document.querySelector('.stromnetz-container');
    const isDark = container && container.classList.contains('dark');
    setTimeout(() => {
      switchBackgroundLottie(isDark);
    }, 600); // Warte bis beide Animationen geladen sind
    
    if (startBtn) {
      startBtn.addEventListener('click', function() {
        // Zoom sofort starten (wie bei Hotspot)
        const scale = 7;
        graphic.classList.add('zooming');
        graphic.style.transform = `scale(${scale})`;
        
        // Startscreen-Overlay auch zoomen für nahtlosen Übergang
        if (startOverlay) {
          startOverlay.classList.add('zooming');
        }
        
        // Startscreen-Inhalt ausblenden
        if (startscreenContent) {
          startscreenContent.style.opacity = '0';
          startscreenContent.style.transition = 'opacity 0.4s ease-out';
        }
        if (startscreenBg) {
          startscreenBg.style.opacity = '0';
          startscreenBg.style.transition = 'opacity 0.4s ease-out';
        }
        
        // Video anzeigen und abspielen
        if (introVideo) {
          introVideo.style.display = 'block';
          introVideo.style.opacity = '0';
          
          // Intro-Video Volume-Button erstellen oder verwenden (falls bereits vorhanden)
          let introMuteBtn = document.querySelector('.intro-video-mute-btn');
          let updateIntroMuteButtonIcon;
          
          if (!introMuteBtn) {
            introMuteBtn = document.createElement('button');
            introMuteBtn.className = 'intro-video-mute-btn';
            introMuteBtn.setAttribute('aria-label', 'Ton ein/aus');
            
            // Funktion zum Aktualisieren des Button-Icons
            updateIntroMuteButtonIcon = function() {
              if (!introVideo || !introMuteBtn) return;
              
              if (introVideo.muted) {
                // Video ist muted → Button zeigt "Unmute" Aktion (Lautsprecher mit Schallwellen)
                introMuteBtn.innerHTML = `
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M19.07 4.93C20.9447 6.81 22 9.3 22 12C22 14.7 20.9447 17.19 19.07 19.07" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M15.54 8.46C16.4774 9.4 17 10.65 17 12C17 13.35 16.4774 14.6 15.54 15.54" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                `;
                introMuteBtn.setAttribute('aria-label', 'Ton einschalten');
              } else {
                // Video ist unmuted → Button zeigt "Mute" Aktion (Lautsprecher mit X)
                introMuteBtn.innerHTML = `
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <line x1="23" y1="9" x2="17" y2="15" stroke="#C81E82" stroke-width="2" stroke-linecap="round"/>
                    <line x1="17" y1="9" x2="23" y2="15" stroke="#C81E82" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                `;
                introMuteBtn.setAttribute('aria-label', 'Ton ausschalten');
              }
            };
            
            introMuteBtn.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              if (introVideo) {
                introVideo.muted = !introVideo.muted;
                updateIntroMuteButtonIcon();
              }
            });
            
            // Button in menu-container_wrapper einfügen
            const menuContainerWrapper = document.getElementById('menu-container_wrapper');
            if (menuContainerWrapper) {
              menuContainerWrapper.appendChild(introMuteBtn);
            }
            
            // Icon initial setzen
            updateIntroMuteButtonIcon();
            
            // Icon aktualisieren, wenn Video-Mute-Status sich ändert
            introVideo.addEventListener('volumechange', function() {
              if (updateIntroMuteButtonIcon) {
                updateIntroMuteButtonIcon();
              }
            });
          }
          
          // "Intro überspringen" Button anzeigen
          if (skipIntroBtn) {
            setTimeout(() => {
              skipIntroBtn.classList.add('visible');
            }, 500);
          }
          
          // Intro-Video Volume-Button anzeigen
          if (introMuteBtn) {
            setTimeout(() => {
              introMuteBtn.classList.add('visible');
            }, 500);
          }
          
          // Video während des Zooms einblenden (nach 300ms Delay, dann 600ms Fade-in)
          setTimeout(() => {
            introVideo.style.opacity = '1';
            // Untertitel explizit aktivieren
            if (introVideo.textTracks && introVideo.textTracks.length > 0) {
              for (let i = 0; i < introVideo.textTracks.length; i++) {
                introVideo.textTracks[i].mode = 'showing';
              }
            }
            introVideo.play();
          }, 300);
          
          // Nach Video-Ende: Herauszoomen und Startscreen ausblenden
          introVideo.addEventListener('ended', function() {
            finishIntro();
          }, { once: true });
        } else {
          // Fallback: Falls Video nicht vorhanden, direkt herauszoomen
          setTimeout(() => {
            finishIntro();
          }, 100);
        }
        //sessionStorage.setItem('stromnetzStarted', '1');
      });
    }
    
    // "Intro überspringen" Button Event-Listener
    if (skipIntroBtn) {
      skipIntroBtn.addEventListener('click', function() {
        // Video stoppen und verstecken
        if (introVideo) {
          introVideo.pause();
          introVideo.currentTime = 0;
          introVideo.style.opacity = '0';
          setTimeout(() => {
            introVideo.style.display = 'none';
          }, 300);
        }
        // Button ausblenden
        skipIntroBtn.classList.remove('visible');
        // Intro-Video Volume-Button ausblenden
        const introMuteBtn = document.querySelector('.intro-video-mute-btn');
        if (introMuteBtn) {
          introMuteBtn.classList.remove('visible');
        }
        // Direkt herauszoomen und Startscreen ausblenden
        finishIntro();
      });
    }

    // Klick auf Overlay oder Button blendet den Hinweis aus
    if (dragInfoOverlay) {
      dragInfoOverlay.addEventListener('click', function() {
        hideDragInfoOverlay();
      });
    }
    if (dragInfoCloseBtn) {
      dragInfoCloseBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        hideDragInfoOverlay();
      });
    }

    // Sobald der Nutzer die Karte wirklich draggt, Hinweis ausblenden
    const dragTarget = document.querySelector('.background-img_wrap');
    if (dragTarget) {
      ['mousedown', 'touchstart'].forEach(evt => {
        dragTarget.addEventListener(evt, function() {
          hideDragInfoOverlay();
        });
      });
    }
  });

  // TECHNISCHE ANSICHT - Video Overlay
  document.querySelectorAll('.technische_ansicht-btn').forEach(btn => {
    btn.addEventListener('click', function() {
      const overlay = document.getElementById('overlay');
      const overlayContent = document.getElementById('overlayContent');
      const graphic = document.querySelector('.graphic-container');

      // Prüfen ob Video bereits offen ist
      const existingVideo = document.getElementById('overlay-bg-video');
      if (existingVideo) {
        // Video ist bereits offen - schließen
        // closeOverlay entfernt
        // Button-Text zurücksetzen
        this.textContent = this.getAttribute('data-original-text') || 'Technische Ansicht';
        this.classList.remove('active');
        return;
      }

      // Original-Text speichern falls noch nicht gesetzt
      if (!this.getAttribute('data-original-text')) {
        this.setAttribute('data-original-text', this.textContent);
      }

      // Button-Text ändern
      this.textContent = 'Zurück zur Übersicht';
      // .technische_ansicht-container aktivieren
      const techContainer = this.closest('.technische_ansicht-container');
      if (techContainer) techContainer.classList.add('active');

      // Zoom
      const scale = 7;
      graphic.classList.add('zooming');
      graphic.style.transform = `scale(${scale})`;

      // Overlay sofort anzeigen!
      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');

      // Klasse zum stromnetz-container hinzufügen
      const stromnetzContainer = document.querySelector('.stromnetz-container');
      if (stromnetzContainer) {
        stromnetzContainer.classList.add('technische_ansicht-active');
      }

      // Vorheriges Bild entfernen
      const oldBg = document.getElementById('overlay-bg-img');
      if (oldBg) oldBg.remove();

      // OverlayContent ausblenden
      overlayContent.style.display = 'none';

      // "Zurück zur Übersicht" Button oben links hinzufügen (falls noch nicht vorhanden)
      let backToOverviewBtn = document.querySelector('.back-to-overview-video');
      if (!backToOverviewBtn) {
        backToOverviewBtn = document.createElement('button');
        backToOverviewBtn.className = 'back-to-overview-video';
        backToOverviewBtn.setAttribute('aria-label', 'Zurück zur Übersicht');
        
        backToOverviewBtn.innerHTML = `
          <span class="icon-back">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="15" stroke-width="1"/>
              <polyline points="18,10 12,16 18,22" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          Zurück zur Übersicht
        `;
        
        backToOverviewBtn.addEventListener('click', function(e) {
          e.preventDefault();
          closeOverlay();
        });
        overlay.appendChild(backToOverviewBtn);
      }

      // Fokus auf Schließen-Button für Tastatur-Navigation
      requestAnimationFrame(() => {
        backToOverviewBtn.focus();
      });

      // Mute/Unmute Button hinzufügen (falls noch nicht vorhanden)
      let muteBtn = document.querySelector('.video-mute-btn');
      let updateMuteButtonIcon;
      
      if (!muteBtn) {
        muteBtn = document.createElement('button');
        muteBtn.className = 'video-mute-btn';
        muteBtn.setAttribute('aria-label', 'Ton ein/aus');
        
        // Funktion zum Aktualisieren des Button-Icons
        // Button zeigt die Aktion an, die beim Klick ausgeführt wird
        updateMuteButtonIcon = function() {
          const video = document.getElementById('overlay-bg-video');
          if (!video || !muteBtn) return;
          
          if (video.muted) {
            // Video ist muted → Button zeigt "Unmute" Aktion (Lautsprecher mit Schallwellen)
            muteBtn.innerHTML = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M19.07 4.93C20.9447 6.81 22 9.3 22 12C22 14.7 20.9447 17.19 19.07 19.07" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15.54 8.46C16.4774 9.4 17 10.65 17 12C17 13.35 16.4774 14.6 15.54 15.54" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            `;
            muteBtn.setAttribute('aria-label', 'Ton einschalten');
          } else {
            // Video ist unmuted → Button zeigt "Mute" Aktion (Lautsprecher mit X)
            muteBtn.innerHTML = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="#C81E82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="23" y1="9" x2="17" y2="15" stroke="#C81E82" stroke-width="2" stroke-linecap="round"/>
                <line x1="17" y1="9" x2="23" y2="15" stroke="#C81E82" stroke-width="2" stroke-linecap="round"/>
              </svg>
            `;
            muteBtn.setAttribute('aria-label', 'Ton ausschalten');
          }
        };
        
        muteBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          const video = document.getElementById('overlay-bg-video');
          if (video) {
            video.muted = !video.muted;
            updateMuteButtonIcon();
          }
        });
        
        // Button in menu-container_wrapper einfügen statt in Overlay
        const menuContainerWrapper = document.getElementById('menu-container_wrapper');
        if (menuContainerWrapper) {
          menuContainerWrapper.appendChild(muteBtn);
        }
      }

      // Video als Hintergrund einfügen (wie bei Hotspot-Bildern)
      const video = document.createElement('video');
      video.className = 'overlay-bg-video';
      video.id = 'overlay-bg-video';
      video.muted = false; // Video mit Ton starten
      video.loop = false;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      
      // Video-Quellen hinzufügen (16x9 und 9x16) - dynamisch basierend auf Dark Mode
      const source16x9 = document.createElement('source');
      source16x9.type = 'video/mp4';
      source16x9.id = 'techVideoSource16x9';
      video.appendChild(source16x9);
      
      const source9x16 = document.createElement('source');
      source9x16.type = 'video/mp4';
      source9x16.id = 'techVideoSource9x16';
      video.appendChild(source9x16);
      
      // Funktion zum Aktualisieren der Video-Quellen basierend auf Dark Mode
      function updateTechVideoSources() {
        const container = document.querySelector('.stromnetz-container');
        const isDark = container && container.classList.contains('dark');
        const videoType = isDark ? 'Nacht' : 'Tag';
        
        source16x9.src = `assets/video/251107_Master-Scene_${videoType}_sound_16x9.mp4`;
        source9x16.src = `assets/video/251107_Master-Scene_${videoType}_sound_9x16.mp4`;
      }
      
      // Video-Quellen initial setzen
      updateTechVideoSources();
      
      // Untertitel hinzufügen (mit mobiler Anpassung)
      attachVttTrack(video, 'assets/video/251201_untertitel.vtt');
      
      video.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: -1;
        opacity: 0;
        transition: opacity 0.3s ease;
      `;
      
      overlay.insertBefore(video, overlay.firstChild);
      
      // Mute-Button Icon initial setzen, nachdem Video erstellt wurde
      if (muteBtn && updateMuteButtonIcon) {
        updateMuteButtonIcon();
      }
      
      // Funktion zum Setzen der richtigen Video-Quelle basierend auf Bildschirmgröße
      function setTechVideoSource() {
        const isMobile = window.innerWidth <= 768;
        if (isMobile && source9x16) {
          // Mobile: 9x16 Video verwenden
          if (video.src !== source9x16.src) {
            video.src = source9x16.src;
            video.load();
          }
        } else if (!isMobile && source16x9) {
          // Desktop: 16x9 Video verwenden
          if (video.src !== source16x9.src) {
            video.src = source16x9.src;
            video.load();
          }
        }
      }
      
      // Video-Quelle setzen
      setTechVideoSource();
      
      // Video-Quelle bei Fenstergrößenänderung aktualisieren
      const resizeHandler = function() {
        setTechVideoSource();
      };
      window.addEventListener('resize', resizeHandler);
      
      // Event-Listener beim Schließen des Overlays entfernen
      const overlayElement = document.getElementById('overlay');
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (!overlayElement.classList.contains('active')) {
              window.removeEventListener('resize', resizeHandler);
            }
          }
        });
      });
      observer.observe(overlayElement, { attributes: true });
      
      // Untertitel aktivieren, sobald das Video geladen ist
      video.addEventListener('loadedmetadata', function() {
        if (video.textTracks && video.textTracks.length > 0) {
          for (let i = 0; i < video.textTracks.length; i++) {
            video.textTracks[i].mode = 'showing';
          }
        }
        // Mute-Button Icon aktualisieren (nach Video-Laden)
        if (muteBtn && updateMuteButtonIcon) {
          updateMuteButtonIcon();
        }
      });
      
      // Mute-Button Icon auch nach dem Setzen der Quelle aktualisieren
      video.addEventListener('loadeddata', function() {
        if (muteBtn && updateMuteButtonIcon) {
          updateMuteButtonIcon();
        }
      });
      
      video.addEventListener('canplay', function() {
        if (video.textTracks && video.textTracks.length > 0) {
          for (let i = 0; i < video.textTracks.length; i++) {
            if (video.textTracks[i].mode !== 'showing') {
              video.textTracks[i].mode = 'showing';
            }
          }
        }
      });
      
      // Mute-Button Icon aktualisieren, wenn Video-Mute-Status sich ändert
      video.addEventListener('volumechange', function() {
        if (muteBtn && updateMuteButtonIcon) {
          updateMuteButtonIcon();
        }
      });
      
      // Initiales Icon setzen, sobald Video erstellt wurde
      if (muteBtn && updateMuteButtonIcon) {
        setTimeout(() => {
          updateMuteButtonIcon();
        }, 100);
      }
      
      // Video weich einblenden
      setTimeout(() => {
        video.style.opacity = '1';
      }, 10);

      // Video mit 0.5 Sekunden Verzögerung starten (mit Ton)
      setTimeout(() => {
        video.autoplay = true;
        const playPromise = video.play();
        
        // Falls Autoplay mit Ton blockiert wird, versuche es erneut nach Benutzerinteraktion
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // Autoplay wurde blockiert - Video ist bereits geladen und kann manuell gestartet werden
            console.log('Autoplay mit Ton wurde blockiert:', error);
            // Video bleibt geladen und kann per Mute-Button oder Klick gestartet werden
          });
        }
      }, 500);

      // Video-Event-Listener für Schließen beim Ende
      setTimeout(() => {
        if (video) {
          video.addEventListener('ended', function() {
            // Optional: Overlay automatisch schließen wenn Video endet
            // closeOverlay();
          });
        }
      }, 100);
    });
  });